// src/components/dashboard/Dashboard.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  PieChart, Pie, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, ScatterChart, Scatter, ZAxis
} from 'recharts';
import {
  BarChart as BarChartLucide,
  Users,
  Target,
  Calendar,
  Shield,
  AlertTriangle,
  Download,
  RefreshCw,
  Info,
  FileText,
  XCircle,
  HelpCircle,
  Eye,
  LineChart,
  Zap,
  Award
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AnalysisSelector from './AnalysisSelector';
import useAnalysisStore from '../stores/useAnalysisStore';
import useAtelier2Store from '../stores/useAtelier2Store';
import useAtelier4Store from '../stores/useAtelier4Store';
import useAtelier5Store from '../stores/useAtelier5Store';
import useAtelier1Store from '../stores/useAtelier1Store';
import stakeholderService from '../services/stakeholderService';
import ecosystemMeasuresService from '../services/ecosystemMeasuresService';
import workshop3Service from '../services/workshop3Service';
import { SECURITY_PILLARS, DREADED_EVENT_IMPACTS } from '../constants';
import { getTranslatedDreadedEventImpacts } from '../utils/translatedConstants';
import Workshop5Charts from './dashboard/Workshop5Charts';
import Workshop1Dashboard from './dashboard/Workshop1Dashboard';
import { normalizeSeverity, normalizePillar, SEVERITY_ORDER } from './dashboard/charts/workshop1Data';
import { calculateNiveau, resolveW2CategoryLabel } from './dashboard/charts/workshop2Data';
import { computeSuggestedThresholds } from './dashboard/charts/workshop3Data';
import Workshop2Dashboard from './dashboard/Workshop2Dashboard';
import Workshop3Dashboard from './dashboard/Workshop3Dashboard';
import Workshop4Dashboard from './dashboard/Workshop4Dashboard';
import useDashboardLayout from './dashboard/useDashboardLayout';
import {
  W1_DEFAULT_LAYOUT,
  W2_DEFAULT_LAYOUT,
  W3_DEFAULT_LAYOUT,
  W4_DEFAULT_LAYOUT,
  W5_DEFAULT_LAYOUT
} from './dashboard/defaultLayouts';
import StrategicSaveModal from './common/StrategicSaveModal';
// Global styles for tooltips and cards
const tooltipGlobalStyles = `
  .recharts-tooltip-wrapper {
    z-index: 10000 !important;
    position: relative !important;
  }
  
  .recharts-tooltip-cursor {
    z-index: 9999 !important;
  }
  
  .recharts-tooltip {
    z-index: 10001 !important;
    position: relative !important;
  }
  
  .recharts-tooltip-wrapper * {
    pointer-events: auto !important;
  }
  
  .status-card {
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    hyphens: auto !important;
    overflow: hidden !important;
  }
  
  .status-card-text {
    max-width: 100% !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    line-height: 1.2 !important;
  }
  
  @media (max-width: 640px) {
    .status-card {
      min-height: 80px !important;
      padding: 8px !important;
    }
    
    .status-card .text-xl {
      font-size: 1rem !important;
    }
    
    .status-card .text-2xl {
      font-size: 1.25rem !important;
    }
    
    .status-card-text {
      font-size: 0.75rem !important;
      -webkit-line-clamp: 1 !important;
    }
  }
  
  .chart-container {
    overflow: hidden !important;
  }
  
  .dashboard-grid {
    display: grid !important;
    gap: 1.5rem !important;
  }
  
  .dashboard-text-safe {
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-wrap: break-word !important;
    max-width: 100% !important;
  }
`;
// Inject global styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = tooltipGlobalStyles;
  if (!document.head.querySelector('[data-tooltip-styles]')) {
    styleElement.setAttribute('data-tooltip-styles', 'true');
    document.head.appendChild(styleElement);
  }
}
// Helper Components
const InfoCard = ({ title, message, icon = FileText, value = null, trend = null, color = 'blue' }) => {
  const colorMap = {
    blue: 'from-blue-500 to-indigo-600 shadow-blue-200/50',
    green: 'from-emerald-500 to-green-600 shadow-emerald-200/50',
    purple: 'from-violet-500 to-purple-600 shadow-violet-200/50',
    amber: 'from-amber-500 to-orange-600 shadow-amber-200/50',
    red: 'from-red-500 to-rose-600 shadow-red-200/50',
  };
  const bgColorMap = {
    blue: 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100',
    green: 'bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-100',
    purple: 'bg-gradient-to-br from-violet-50 to-purple-50 border-violet-100',
    amber: 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100',
    red: 'bg-gradient-to-br from-red-50 to-rose-50 border-red-100',
  };
  const iconColorMap = {
    blue: 'text-blue-600',
    green: 'text-emerald-600',
    purple: 'text-violet-600',
    amber: 'text-amber-600',
    red: 'text-red-600',
  };
  const textColorMap = {
    blue: 'text-white',
    green: 'text-white',
    purple: 'text-white',
    amber: 'text-white',
    red: 'text-white',
  };

  return (
    <motion.div
      whileHover={{ y: -5, scale: 1.02, boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}
      transition={{ duration: 0.2 }}
      className={`${bgColorMap[color]} p-5 rounded-xl border shadow-lg relative overflow-hidden flex flex-col justify-between min-h-[150px]`}
    >
      <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white/10"></div>
      <div className="absolute -right-4 -bottom-4 w-16 h-16 rounded-full bg-white/10"></div>
      <div className="relative z-10">
        <div className="flex items-center mb-2">
          <div className={`p-2 rounded-lg bg-white/20 mr-3`}>
            {React.createElement(icon, { size: 18, className: `${textColorMap[color]}` })}
          </div>
          <h3 className={`text-sm font-medium ${textColorMap[color]}`}>{title}</h3>
        </div>
      </div>
      {value !== null ? (
        <div className="mt-3 relative z-10">
          <span className={`text-4xl font-bold ${textColorMap[color]}`}>{value}</span>
        </div>
      ) : (
        <div className="flex items-center justify-center text-center text-xs text-white/80 mt-1 flex-grow bg-white/10 p-3 rounded-lg backdrop-blur-sm relative z-10">
          {React.createElement(icon, { size: 16, className: "mr-1 flex-shrink-0" })}
          <span>{message}</span>
        </div>
      )}
      <div className={`absolute inset-0 bg-gradient-to-br ${colorMap[color]} opacity-90 z-0`}></div>
    </motion.div>
  );
};
const EmptyChartPlaceholder = ({ title, icon = BarChartLucide }) => {
  const { t } = useTranslation();
  return (
    <motion.div
      whileHover={{ y: -3, boxShadow: '0 15px 30px -5px rgba(0, 0, 0, 0.1), 0 10px 15px -6px rgba(0, 0, 0, 0.05)' }}
      transition={{ duration: 0.2 }}
      className="bg-white p-5 rounded-xl border border-gray-100 shadow-lg overflow-hidden relative"
      style={{
        backgroundImage: 'radial-gradient(circle at 100% 100%, rgba(243, 244, 246, 0.4) 0%, transparent 40%)',
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01)',
      }}
    >
      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-full -mr-12 -mt-12 opacity-50"></div>
      <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-green-50 to-emerald-50 rounded-full -ml-8 -mb-8 opacity-50"></div>
      <div className="relative">
        <div className="flex items-center mb-4">
          <div className="p-2 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 mr-3">
            {React.createElement(icon, { size: 20, className: "text-indigo-600" })}
          </div>
          <h3 className="font-semibold text-gray-800">{title}</h3>
        </div>
        <div className="h-[300px] flex flex-col items-center justify-center text-center bg-gradient-to-b from-gray-50 to-white rounded-lg border border-gray-100">
          {React.createElement(icon, { size: 48, className: `text-gray-300 mb-3 opacity-50` })}
          <p className="text-sm text-gray-400">{t('dashboard.empty.noSecurityMeasures')}</p>
          <button className="mt-4 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg text-sm hover:bg-indigo-100 transition-colors">
            {t('common.add')}
          </button>
        </div>
      </div>
    </motion.div>
  );
};
const ChartContainer = ({ title, icon, children }) => (
  <motion.div
    whileHover={{ y: -3, boxShadow: '0 15px 30px -5px rgba(0, 0, 0, 0.1), 0 10px 15px -6px rgba(0, 0, 0, 0.05)' }}
    transition={{ duration: 0.2 }}
    className="bg-white p-3 rounded-xl border border-gray-100 shadow-lg relative h-full flex flex-col"
    style={{
      backgroundImage: 'radial-gradient(circle at 100% 100%, rgba(243, 244, 246, 0.4) 0%, transparent 40%)',
      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01)',
    }}
  >
    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-full -mr-12 -mt-12 opacity-50"></div>
    <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-green-50 to-emerald-50 rounded-full -ml-8 -mb-8 opacity-50"></div>
    <div className="relative flex-1 min-h-0 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          <div className="p-2 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 mr-3">
            {React.createElement(icon, { size: 20, className: "text-indigo-600" })}
          </div>
          <h3 className="font-semibold text-gray-800">{title}</h3>
        </div>
      </div>
      <div className="flex-1 min-h-0">
        {children}
      </div>
    </div>
  </motion.div>
);
// Shared animation variants (module scope so DashboardSection can use them)
const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};
// Reusable per-workshop section header (Edit / Reset / Show-all layout buttons).
// `title` is the left-side header node (badge + heading + subtitle); the layout
// buttons on the right are identical for every workshop.
const DashboardSection = ({ title, editMode, onToggleEdit, onReset, onShowAll, hiddenCount, children }) => {
  const { t } = useTranslation();
  return (
    <motion.div variants={itemVariants} className="space-y-8">
      <div className="flex items-center space-x-4 mb-6">
        {title}
        <button
          type="button"
          onClick={onToggleEdit}
          className="ml-auto px-3 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 flex-shrink-0"
        >
          {t(editMode ? 'dashboard.layout.done' : 'dashboard.layout.edit', 'Edit layout')}
        </button>
        <button
          type="button"
          onClick={onReset}
          className="px-3 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 flex-shrink-0"
        >
          {t('dashboard.layout.reset', 'Reset')}
        </button>
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={onShowAll}
            className="px-3 py-1 rounded-md text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 flex-shrink-0"
          >
            {t('dashboard.layout.hiddenBadge', { count: hiddenCount })} · {t('dashboard.layout.showAll', 'Show all')}
          </button>
        )}
      </div>
      {children}
    </motion.div>
  );
};
const useTabSwitcher = () => {
  const switchToTab = (tabName) => {
    localStorage.setItem('ebiosrm_switch_to_tab', tabName);
    window.dispatchEvent(new CustomEvent('ebiosrm_switch_tab'));
  };
  return { switchToTab };
};
// Color palettes for charts
const STATUS_COLORS = {
  implemented: '#10B981',
  partial: '#F59E0B',
  planned: '#3B82F6',
  apply_before: '#8B5CF6',
  abandoned: '#EF4444',
  undefined: '#94A3B8'
};
const RISK_LEVEL_COLORS = {
  high: '#DC2626',
  medium: '#F59E0B',
  low: '#10B981',
  undefined: '#94A3B8'
};
const COVERAGE_COLORS = {
  high: '#10B981',
  medium: '#F59E0B',
  low: '#3B82F6',
  undefined: '#94A3B8'
};
const SEVERITY_COLORS = {
  catastrophic: '#1E293B',
  critical: '#EF4444',
  major: '#F97316',
  moderate: '#FBBF24',
  minor: '#10B981',
  undefined: '#94A3B8'
};
const ROLE_COLORS = [
  '#60A5FA', '#34D399', '#FBBF24', '#F87171', '#A78BFA',
  '#22D3EE', '#F472B6', '#C084FC', '#4ADE80', '#F59E0B'
];
const PILLAR_COLORS = [
  '#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#06B6D4', '#EC4899', '#22C55E'
];
const ModernNavigationTabs = ({ activeSection, setActiveSection, context, t }) => {
  const tabs = [
    { id: 'overview', label: t('dashboard.tabs.overview', 'Overview') }
  ];

  return (
    <div className="mb-6 border-b border-gray-200 overflow-x-auto" role="tablist" aria-label={t('dashboard.tabs.aria', 'Dashboard sections')}>
      <div className="flex gap-2 min-w-max">
        {tabs.map(tab => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeSection === tab.id}
            tabIndex={activeSection === tab.id ? 0 : -1}
            onClick={() => setActiveSection(tab.id)}
            className={`px-3 py-2 rounded-md text-sm font-medium ${activeSection === tab.id ? 'text-indigo-600 bg-indigo-50' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
};
// Main Dashboard Component
const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const { switchToTab } = useTabSwitcher();
  const { user } = useAuth(); // Need user for admin check
  const [showStrategicSave, setShowStrategicSave] = useState(false);

  const analysisStore = useAnalysisStore();
  const atelier1Store = useAtelier1Store();
  const atelier2Store = useAtelier2Store();
  const atelier5Store = useAtelier5Store();
  const atelier4Store = useAtelier4Store();
  const isWorkshopDataLoading = analysisStore.isWorkshopDataLoading;
  const workshopDataError = analysisStore.workshopDataError;
  const workshop5Synthesis = atelier5Store.synthesisData;
  const loadWorkshop4DashboardData = async (analysisId) => {
    if (!analysisId) return;
    return useAtelier4Store.getState().loadDashboardData(analysisId);
  };
  const updateAnalysis = useAnalysisStore.getState().updateAnalysis;

  const currentAnalysis = analysisStore.currentAnalysis;
  const currentContextData = atelier1Store.contextData;
  const currentBusinessValues = atelier1Store.businessValues;
  const currentDreadedEvents = atelier1Store.dreadedEvents;
  const currentSecurityFramework = atelier1Store.securityFramework;
  const currentAnalysisControlPlan = atelier1Store.controlPlan;
  const currentRiskTreatments = atelier1Store.riskTreatments;
  const currentAttackPaths = atelier4Store.attackPaths;
  const currentOperationalScenarios = atelier4Store.operationalScenarios;
  const workshop4DashboardData = atelier4Store.dashboardData;
  const getSourcesDeRisque = atelier2Store.getSourcesDeRisque;
  const getSelectedCouplesSROV = atelier2Store.getSelectedCouplesSROV;

  const handleStrategicSave = async (justification) => {
    if (!currentAnalysis?.id) return;

    try {
      const result = await updateAnalysis(currentAnalysis.id, {
        strategic: true,
        justification: justification
      });

      if (result.success) {
        setShowStrategicSave(false);
        // Toast suggestion? Or just rely on updateAnalysis context handling
      }
    } catch (error) {
      console.error('Failed to create strategic version', error);
    }
  };

  const [activeSection, setActiveSection] = useState('overview');
  const [layoutEdit, setLayoutEdit] = useState({});
  const toggleLayoutEdit = (key) => {
    setLayoutEdit((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const analysisId = currentAnalysis?.id;
  const [w1Layouts, handleW1LayoutChange, resetW1Layout, toggleW1Hidden, showAllW1, w1HiddenCount] = useDashboardLayout(analysisId, 'w1', W1_DEFAULT_LAYOUT);
  const [w2Layouts, handleW2LayoutChange, resetW2Layout, toggleW2Hidden, showAllW2, w2HiddenCount] = useDashboardLayout(analysisId, 'w2', W2_DEFAULT_LAYOUT);
  const [w3Layouts, handleW3LayoutChange, resetW3Layout, toggleW3Hidden, showAllW3, w3HiddenCount] = useDashboardLayout(analysisId, 'w3', W3_DEFAULT_LAYOUT);
  const [w4Layouts, handleW4LayoutChange, resetW4Layout, toggleW4Hidden, showAllW4, w4HiddenCount] = useDashboardLayout(analysisId, 'w4', W4_DEFAULT_LAYOUT);
  const [w5Layouts, handleW5LayoutChange, resetW5Layout, toggleW5Hidden, showAllW5, w5HiddenCount] = useDashboardLayout(analysisId, 'w5', W5_DEFAULT_LAYOUT);

  const context = currentContextData || {};
  const bValues = currentBusinessValues || [];
  const events = currentDreadedEvents || [];
  const frameworkData = currentSecurityFramework || null;

  const controlsData = [];
  if (currentAnalysisControlPlan?.planData?.plan) {
    Object.entries(currentAnalysisControlPlan.planData.plan).forEach(([key, ruleEventData]) => {
      if (ruleEventData?.controls && Array.isArray(ruleEventData.controls)) {
        ruleEventData.controls.forEach(control => {
          controlsData.push({
            id: control.id,
            name: control.name,
            description: control.description,
            type: control.type || 'Standard',
            status: control.decision || 'planned',
            responsiblePerson: control.responsiblePerson
          });
        });
      }
    });
  }

  const treatmentsData = currentRiskTreatments || [];

  const participantRolesData = context?.participants?.length > 0
    ? context.participants.reduce((acc, p) => {
      let role = p.position === "OTHER" && p.customPosition ? p.customPosition : (p.position || 'Undefined');
      const existing = acc.find(item => item.name === role);
      if (existing) existing.value += 1;
      else acc.push({ name: role, value: 1 });
      return acc;
    }, [])
    : [];

  const securityPillarEventsData = SECURITY_PILLARS.map(pillar => {
    const normalizedTarget = normalizePillar(pillar.id);
    let count = 0;

    if (events?.length > 0) {
      events.forEach(event => {
        if (normalizePillar(event.securityPillar) === normalizedTarget) {
          count += 1;
        }
      });
    }

    return {
      subject: pillar.name,
      value: count,
      fullMark: 10
    };
  });

  const businessValuePillarsData = bValues?.length > 0
    ? bValues.reduce((acc, bv) => {
      if (Array.isArray(bv.securityPillars)) {
        bv.securityPillars.forEach(pillar => {
          const pillarName = pillar || 'Undefined';
          const existing = acc.find(item => item.name === pillarName);
          if (existing) existing.value += 1;
          else acc.push({ name: pillarName, value: 1 });
        });
      }
      return acc;
    }, [])
    : [];

  const [w2Sources, setW2Sources] = useState([]);
  const [w2Mappings, setW2Mappings] = useState([]);
  const [w2Loading, setW2Loading] = useState(false);
  const [w2CustomCategories, setW2CustomCategories] = useState([]);
  const [w2HiddenCategoryIds, setW2HiddenCategoryIds] = useState([]);

  // Atelier 3 Ecosystem Data
  const [stakeholdersData, setStakeholdersData] = useState([]);
  const [stakeholdersLoading, setStakeholdersLoading] = useState(false);
  const [stakeholderThresholds, setStakeholderThresholds] = useState(null);
  const [ecosystemMeasuresData, setEcosystemMeasuresData] = useState([]);
  const [ecosystemMeasuresLoading, setEcosystemMeasuresLoading] = useState(false);
  const [attackPathsData, setAttackPathsData] = useState([]);

  useEffect(() => {
    const loadWorkshop2 = async () => {
      if (!currentAnalysis?.id) {
        setW2Sources([]);
        setW2Mappings([]);
        setW2CustomCategories([]);
        setW2HiddenCategoryIds([]);
        return;
      }

      try {
        setW2Loading(true);
        const [sources, mappings] = await Promise.all([
          getSourcesDeRisque?.(currentAnalysis.id),
          getSelectedCouplesSROV?.(currentAnalysis.id)
        ]);
        setW2Sources(Array.isArray(sources) ? sources : []);
        setW2Mappings(Array.isArray(mappings) ? mappings : []);
        try {
          const threatData = await useAtelier2Store.getState().loadThreatCategories(currentAnalysis.id);
          setW2CustomCategories(threatData?.definitions?.custom || []);
          setW2HiddenCategoryIds(threatData?.hidden || []);
        } catch (threatError) {
          setW2CustomCategories([]);
          setW2HiddenCategoryIds([]);
        }
      } catch (e) {
        setW2Sources([]);
        setW2Mappings([]);
      } finally {
        setW2Loading(false);
      }
    };

    loadWorkshop2();
  }, [currentAnalysis?.id, getSourcesDeRisque, getSelectedCouplesSROV]);

  // Load Atelier 3 stakeholder data
  useEffect(() => {
    const loadStakeholdersData = async () => {
      if (!currentAnalysis?.id) {
        setStakeholdersData([]);
        return;
      }

      try {
        setStakeholdersLoading(true);

        const [stakeholdersRes, thresholdsRes] = await Promise.all([
          stakeholderService.getStakeholders(currentAnalysis.id),
          stakeholderService.getThresholds(currentAnalysis.id)
        ]);

        if (stakeholdersRes.success && Array.isArray(stakeholdersRes.data)) {
          setStakeholdersData(stakeholdersRes.data);
        } else {
          setStakeholdersData([]);
        }

        if (thresholdsRes.success && thresholdsRes.data) {
          setStakeholderThresholds(thresholdsRes.data);
        }
      } catch (error) {
        console.error('Error loading stakeholders:', error);
        setStakeholdersData([]);
      } finally {
        setStakeholdersLoading(false);
      }
    };

    loadStakeholdersData();
  }, [currentAnalysis?.id]);

  // Load Atelier 3 ecosystem measures data
  useEffect(() => {
    const loadEcosystemMeasuresData = async () => {
      if (!currentAnalysis?.id) {
        setEcosystemMeasuresData([]);
        return;
      }

      try {
        setEcosystemMeasuresLoading(true);
        const response = await ecosystemMeasuresService.getEcosystemMeasures(currentAnalysis.id);
        if (response.success && Array.isArray(response.data)) {
          setEcosystemMeasuresData(response.data);
        } else {
          setEcosystemMeasuresData([]);
        }
      } catch (error) {
        console.error('Error loading ecosystem measures:', error);
        setEcosystemMeasuresData([]);
      } finally {
        setEcosystemMeasuresLoading(false);
      }
    };

    loadEcosystemMeasuresData();
  }, [currentAnalysis?.id]);

  // Load Atelier 3 attack paths (links measures to stakeholders for residual risk)
  useEffect(() => {
    const loadAttackPathsData = async () => {
      if (!currentAnalysis?.id) {
        setAttackPathsData([]);
        return;
      }

      try {
        const response = await workshop3Service.getAttackPaths(currentAnalysis.id);
        if (response.success && Array.isArray(response.data)) {
          setAttackPathsData(response.data);
        } else {
          setAttackPathsData([]);
        }
      } catch (error) {
        console.error('Error loading attack paths:', error);
        setAttackPathsData([]);
      }
    };

    loadAttackPathsData();
  }, [currentAnalysis?.id]);

  // Load Atelier 5 synthesis (same source as Synthèse des scénarios de risque)
  useEffect(() => {
    if (!currentAnalysis?.id) return;
    const st = useAtelier5Store.getState();
    if (!st.synthesisData && !st.isLoading && !st.isDataLoaded) {
      st.loadData(currentAnalysis.id);
    }
  }, [currentAnalysis?.id]);

  const w2SourcesByCategoryData = Array.isArray(w2Sources) && w2Sources.length > 0
    ? w2Sources.reduce((acc, s) => {
      const sourceData = s.data?.sources?.[0] || s;
      const raw = sourceData.category || s.category || s.sourceCategory || 'undefined';
      const key = String(raw).trim().toLowerCase();

      const categoryName = resolveW2CategoryLabel(key, { customCategories: w2CustomCategories, t });

      const existing = acc.find(d => d.key === key);
      if (existing) {
        existing.value += 1;
        existing.sources.push({
          name: sourceData.name || 'Source sans nom',
          description: sourceData.description || '',
          motivation: sourceData.motivation || 'Non défini',
          activite: sourceData.activite || 'Non défini',
          ressources: sourceData.ressources || 'Non défini'
        });
      } else {
        acc.push({
          key,
          name: categoryName,
          value: 1,
          sources: [{
            name: sourceData.name || 'Source sans nom',
            description: sourceData.description || '',
            motivation: sourceData.motivation || 'Non défini',
            activite: sourceData.activite || 'Non défini',
            ressources: sourceData.ressources || 'Non défini'
          }]
        });
      }
      return acc;
    }, [])
    : [];

  const w2MappingsBySourceData = Array.isArray(w2Sources) && w2Sources.length > 0
    ? w2Sources.reduce((acc, s) => {
      const sourceData = s.data?.sources?.[0] || s;
      const sourceName = sourceData.name || s.sourceName || s.source?.name || 'Source inconnue';
      const sourceId = sourceData.id || s.sourceId || s.source?.id || s.id;

      if (!sourceName || sourceName === 'Source inconnue' || !sourceId) {
        return acc;
      }

      const riskLevel = calculateNiveau(
        sourceData.motivation,
        sourceData.activite,
        sourceData.ressources
      );

      const mappingCount = Array.isArray(w2Mappings)
        ? w2Mappings.filter(m => {
          const matchById = m.sourceId === sourceId || m.source?.id === sourceId;
          const matchByName = m.sourceId === sourceName || m.source?.name === sourceName;
          return matchById || matchByName;
        }).length
        : 1;

      if (mappingCount === 0 && !sourceData.description?.trim()) {
        return acc;
      }

      const existing = acc.find(d => d.sourceId === sourceId || d.name === sourceName);
      if (existing) {
        existing.value = Math.max(existing.value, mappingCount);
        return acc;
      }

      acc.push({
        sourceId,
        name: sourceName,
        value: Math.max(mappingCount, 1),
        riskLevel,
        category: sourceData.category || 'Non défini',
        description: sourceData.description || '',
        objectifVise: sourceData.objectifVise || 'Non spécifié',
        motivation: sourceData.motivation || 'Non défini',
        activite: sourceData.activite || 'Non défini',
        ressources: sourceData.ressources || 'Non défini'
      });
      return acc;
    }, []).sort((a, b) => {
      if (a.riskLevel !== b.riskLevel) {
        return b.riskLevel - a.riskLevel;
      }
      return b.value - a.value;
    }).slice(0, 10)
    : [];

  // Process stakeholders data for ecosystem analysis
  const calculateThreatLevel = (stakeholder) => {
    const { dependency = 1, penetration = 1, cyberMaturity = 1, trust = 1 } = stakeholder;
    return (dependency * penetration) / (cyberMaturity * trust);
  };

  const getThreatZone = (threatLevel, thresholds) => {
    const { danger = 3, control = 1.5, watch = 0.5 } = thresholds || {};
    if (threatLevel >= danger) return 'danger';
    if (threatLevel >= control) return 'control';
    if (threatLevel >= watch) return 'watch';
    return 'outside';
  };

  const stakeholderCategoriesData = stakeholdersData.length > 0
    ? stakeholdersData.reduce((acc, stakeholder) => {
      const category = stakeholder.category || 'undefined';
      const categoryNames = {
        'client': 'Clients',
        'partner': 'Partenaires',
        'provider': 'Fournisseurs',
        'technical': 'Services techniques',
        'business': 'Services métier',
        'subsidiary': 'Filiales'
      };
      const categoryName = categoryNames[category] || 'Non défini';

      const existing = acc.find(item => item.category === category);
      if (existing) {
        existing.value += 1;
      } else {
        acc.push({
          name: categoryName,
          value: 1,
          category
        });
      }
      return acc;
    }, [])
    : [
      { name: 'Clients', value: 3, category: 'client' },
      { name: 'Partenaires', value: 2, category: 'partner' },
      { name: 'Fournisseurs', value: 4, category: 'provider' },
      { name: 'Services techniques', value: 2, category: 'technical' },
      { name: 'Services métier', value: 1, category: 'business' }
    ];

  const threatZoneDistribution = stakeholdersData.length > 0
    ? stakeholdersData.reduce((acc, stakeholder) => {
      const threatLevel = calculateThreatLevel(stakeholder);
      const zone = getThreatZone(threatLevel, stakeholderThresholds);

      const existing = acc.find(item => item.zone === zone);
      if (existing) {
        existing.count += 1;
      } else {
        acc.push({ zone, count: 1 });
      }
      return acc;
    }, []).map(item => ({
      ...item,
      percentage: Math.round((item.count / stakeholdersData.length) * 100)
    }))
    : [
      { zone: 'danger', count: 3, percentage: 25 },
      { zone: 'control', count: 4, percentage: 35 },
      { zone: 'watch', count: 4, percentage: 30 },
      { zone: 'outside', count: 1, percentage: 10 }
    ];

  const cyberMaturityDistribution = stakeholdersData.length > 0
    ? stakeholdersData.reduce((acc, stakeholder) => {
      const fiabiliteCyber = (stakeholder.cyberMaturity || 1) * (stakeholder.trust || 1);
      let level;
      if (fiabiliteCyber >= 12) level = 'Élevé';
      else if (fiabiliteCyber >= 8) level = 'Moyen';
      else if (fiabiliteCyber >= 4) level = 'Faible';
      else level = 'Critique';

      const existing = acc.find(item => item.level === level);
      if (existing) {
        existing.count += 1;
      } else {
        acc.push({ level, count: 1 });
      }
      return acc;
    }, [])
    : [
      { level: 'Critique', count: 2 },
      { level: 'Faible', count: 3 },
      { level: 'Moyen', count: 4 },
      { level: 'Élevé', count: 3 }
    ];

  // Process ecosystem measures data for real statistics
  const ecosystemMeasuresStats = ecosystemMeasuresData.length > 0
    ? ecosystemMeasuresData.reduce((acc, measure) => {
      const category = measure.category || measure.type || 'other';
      const categoryMap = {
        'preventive': 'Préventives',
        'detective': 'Détectives',
        'corrective': 'Correctives',
        'recovery': 'Récupération',
        'protection': 'Préventives',
        'detection': 'Détectives',
        'response': 'Correctives',
        'other': 'Autres'
      };

      const mappedCategory = categoryMap[category.toLowerCase()] || 'Autres';
      const existing = acc.find(item => item.label === mappedCategory);

      if (existing) {
        existing.count += 1;
      } else {
        const iconMap = {
          'Préventives': '🛡️',
          'Détectives': '🔍',
          'Correctives': '🔧',
          'Récupération': '🔄',
          'Autres': '📋'
        };
        const colorMap = {
          'Préventives': 'text-blue-600',
          'Détectives': 'text-purple-600',
          'Correctives': 'text-green-600',
          'Récupération': 'text-orange-600',
          'Autres': 'text-gray-600'
        };

        acc.push({
          label: mappedCategory,
          count: 1,
          icon: iconMap[mappedCategory],
          color: colorMap[mappedCategory]
        });
      }
      return acc;
    }, [])
    : [
      { label: 'Préventives', count: 0, icon: '🛡️', color: 'text-blue-600' },
      { label: 'Détectives', count: 0, icon: '🔍', color: 'text-purple-600' },
      { label: 'Correctives', count: 0, icon: '🔧', color: 'text-green-600' },
      { label: 'Récupération', count: 0, icon: '🔄', color: 'text-orange-600' }
    ];

  const securityControlStatusData = controlsData.length > 0 ?
    Object.keys(STATUS_COLORS).reduce((acc, status) => {
      const count = controlsData.filter(control =>
        control.status === status ||
        (status === 'undefined' && !control.status)
      ).length;
      if (count > 0) {
        acc.push({
          name: status === 'implemented' ? t('dashboard.statusLabels.implemented') :
            status === 'partial' ? t('dashboard.statusLabels.partial') :
              status === 'planned' ? t('dashboard.statusLabels.planned') :
                status === 'apply_before' ? t('dashboard.statusLabels.toApply') :
                  status === 'abandoned' ? t('dashboard.statusLabels.toAbandon') : 'Non défini',
          value: count,
          color: STATUS_COLORS[status]
        });
      }
      return acc;
    }, []) : [
      { name: t('dashboard.statusLabels.implemented'), value: 0, color: STATUS_COLORS.implemented },
      { name: t('dashboard.statusLabels.partial'), value: 0, color: STATUS_COLORS.partial },
      { name: t('dashboard.statusLabels.planned'), value: 0, color: STATUS_COLORS.planned },
      { name: t('dashboard.statusLabels.toApply'), value: 0, color: STATUS_COLORS.apply_before },
      { name: t('dashboard.statusLabels.toAbandon'), value: 0, color: STATUS_COLORS.abandoned },
    ];

  const eventsBySeverityData = events.length > 0 ?
    events.reduce((acc, event) => {
      const severity = event.severity || event.gravity || 'undefined';

      const severityLabel = normalizeSeverity(severity);
      const existing = acc.find(item => item.name === severityLabel);
      if (existing) {
        existing.value += 1;
      } else {
        const severityColors = {
          'Catastrophique': '#1F2937',
          'Critique': '#DC2626',
          'Majeur': '#EA580C',
          'Modéré': '#D97706',
          'Mineur': '#059669',
          'Non défini': '#94A3B8'
        };

        const color = severityColors[severityLabel] || '#94A3B8';
        acc.push({
          name: severityLabel,
          value: 1,
          color,
          originalSeverity: severity
        });
      }
      return acc;
    }, []).sort((a, b) => {
      return (SEVERITY_ORDER[b.name] || 0) - (SEVERITY_ORDER[a.name] || 0);
    }) : [];

  const eventsByImpactData = events.length > 0 ?
    events.reduce((acc, event) => {
      const impactArray = event.impacts || (event.impact ? [event.impact] : []);
      impactArray.forEach(impactValue => {
        const key = typeof impactValue === 'string' ? impactValue : (impactValue.impact || impactValue.value || impactValue.name || '');
        const translatedImpacts = getTranslatedDreadedEventImpacts();
        const impact = translatedImpacts.find(imp => imp.value === key);
        const impactLabel = impact ? impact.label : (key || 'Non spécifié');
        const existing = acc.find(item => item.name === impactLabel);
        if (existing) {
          existing.value += 1;
        } else {
          const impactColors = {
            'missions_services': '#3B82F6',
            'humain_materiel_environnemental': '#EF4444',
            'gouvernance': '#8B5CF6',
            'financier': '#10B981',
            'juridique': '#F59E0B',
            'image_confiance': '#EC4899',
          };
          const color = impactColors[key] || '#94A3B8';
          acc.push({ name: impactLabel, value: 1, color });
        }
      });
      return acc;
    }, []) : [];

  const eventsByBusinessValueData = [];
  if (events.length > 0 && bValues.length > 0) {
    const bValueMap = bValues.reduce((map, bv) => {
      map[bv.id] = bv.name;
      return map;
    }, {});

    events.forEach(event => {
      if (event.businessValue) {
        const bvName = bValueMap[event.businessValue] || `ID: ${event.businessValue}`;
        const existing = eventsByBusinessValueData.find(item => item.name === bvName);
        if (existing) {
          existing.value += 1;
        } else {
          eventsByBusinessValueData.push({ name: bvName, value: 1 });
        }
      }
    });
  }

  const tabVariants = {
    active: {
      backgroundColor: '#EFF6FF',
      color: '#1D4ED8',
      borderColor: '#93C5FD',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
    },
    inactive: {
      backgroundColor: '#F3F4F6',
      color: '#4B5563',
      borderColor: '#E5E7EB',
      boxShadow: 'none'
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="modern-card modern-fade-in p-8"
    >
      {/* Header */}
      <div className="dashboard-header-enhanced mb-8">
        {/* Decorative Elements */}
        <div className="dashboard-header-decoration dashboard-header-decoration-1"></div>
        <div className="dashboard-header-decoration dashboard-header-decoration-2"></div>
        <div className="dashboard-header-decoration dashboard-header-decoration-3"></div>

        <div className="dashboard-header-content">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="dashboard-header-title">
                  {t('dashboard.title')}
                </h2>
              </div>
              <p className="dashboard-header-subtitle">{t('dashboard.subtitle')}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <motion.div
                className="dashboard-date-badge"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Calendar size={18} />
                <span>
                  {new Date().toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Animated Bottom Line */}
        <div className="dashboard-header-line"></div>
      </div>

      {/* Analysis Selector - Integrated */}
      <motion.div
        className="mb-8"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <AnalysisSelector />
      </motion.div>

      {/* Loading State */}
      {isWorkshopDataLoading && (
        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          className="modern-card p-8 my-8 flex items-center justify-center bg-gradient-to-r from-blue-50 to-indigo-50"
        >
          <RefreshCw size={24} className="animate-spin text-blue-600 mr-4" />
          <div>
            <p className="text-blue-800 font-semibold text-lg">{t('dashboard.status.loading')}</p>
            <p className="text-blue-600 text-sm mt-1">{t('dashboard.status.loadingDetails', 'Loading analysis data...')}</p>
          </div>
        </motion.div>
      )}

      {/* Error state */}
      {workshopDataError && !isWorkshopDataLoading && (
        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          className="modern-card p-6 my-8 bg-gradient-to-r from-red-50 to-rose-50 border-red-200"
        >
          <div className="flex items-start">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
              <XCircle size={24} className="text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-red-800 font-semibold text-lg">{t('dashboard.status.error')}</h3>
              <p className="text-red-700 text-sm mt-1">{t('dashboard.status.errorDetails')}</p>
            </div>
            <button className="modern-btn modern-btn-error text-sm">
              {t('dashboard.status.retry')}
            </button>
          </div>
        </motion.div>
      )}

      {/* Main Content */}
      {!isWorkshopDataLoading && currentAnalysis && (
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSection}
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            {/* Navigation Tabs */}
            <ModernNavigationTabs
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              t={t}
            />

            {/* Main Content based on activeSection */}
            <div className="space-y-8">
              {/* Overview Section */}
              {activeSection === 'overview' && (
                <motion.div variants={containerVariants}>
                  {/* Main Dashboard Layout - Organized by EBIOS RM Workshops */}
                  <div className="space-y-12">

                    {/* WORKSHOP 1: Cadrage et socle de sécurité - Tremor Version */}
                    <DashboardSection
                      title={(
                        <>
                          <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                            <span className="text-white font-bold text-lg">1</span>
                          </div>
                          <div>
                            <h2 className="text-2xl font-bold text-gray-800">{t('dashboard.workshop1.title', 'Workshop 1: Cadrage et socle de sécurité')}</h2>
                            <p className="text-gray-600">{t('dashboard.workshop1.subtitle', 'Définition du périmètre et identification des biens supports')}</p>
                          </div>
                        </>
                      )}
                      editMode={!!layoutEdit.w1}
                      onToggleEdit={() => toggleLayoutEdit('w1')}
                      onReset={resetW1Layout}
                      onShowAll={showAllW1}
                      hiddenCount={w1HiddenCount}
                    >
                      {/* New Tremor-based Professional Dashboard */}
                      <Workshop1Dashboard
                        contextData={context}
                        businessValues={bValues}
                        dreadedEvents={events}
                        securityFramework={frameworkData}
                        securityControlStatusData={securityControlStatusData}
                        controlsData={controlsData}
                        participantRolesData={participantRolesData}
                        businessValuePillarsData={businessValuePillarsData}
                        eventsBySeverityData={eventsBySeverityData}
                        securityPillarEventsData={securityPillarEventsData}
                        eventsByImpactData={eventsByImpactData}
                        eventsByBusinessValueData={eventsByBusinessValueData}
                        editMode={!!layoutEdit.w1}
                        layouts={w1Layouts}
                        onLayoutChange={handleW1LayoutChange}
                        onToggleHidden={toggleW1Hidden}
                      />
                    </DashboardSection>

                    {/* WORKSHOP 2: Sources de risque - Tremor Version */}
                    <DashboardSection
                      title={(
                        <>
                          <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl shadow-lg">
                            <span className="text-white font-bold text-lg">2</span>
                          </div>
                          <div>
                            <h2 className="text-2xl font-bold text-gray-800">{t('dashboard.workshop2.title', 'Workshop 2: Sources de risque')}</h2>
                            <p className="text-gray-600">{t('dashboard.workshop2.subtitle', 'Identification des événements redoutés et sources de risque')}</p>
                          </div>
                        </>
                      )}
                      editMode={!!layoutEdit.w2}
                      onToggleEdit={() => toggleLayoutEdit('w2')}
                      onReset={resetW2Layout}
                      onShowAll={showAllW2}
                      hiddenCount={w2HiddenCount}
                    >
                      {/* New Tremor-based Professional Dashboard */}
                      <Workshop2Dashboard
                        dreadedEvents={events}
                        w2Sources={w2Sources}
                        w2Mappings={w2Mappings}
                        customCategories={w2CustomCategories}
                        hiddenCategoryIds={w2HiddenCategoryIds}
                        w2Loading={w2Loading}
                        editMode={!!layoutEdit.w2}
                        layouts={w2Layouts}
                        onLayoutChange={handleW2LayoutChange}
                        onToggleHidden={toggleW2Hidden}
                      />
                    </DashboardSection>

                    {/* WORKSHOP 2 LEGACY: Original inline charts (hidden - never mounted to avoid 0-size ResponsiveContainer warnings) */}
                    {false && (
                      <motion.div variants={itemVariants} className="space-y-8 hidden">
                      <div className="flex items-center space-x-4 mb-6">
                        <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl shadow-lg">
                          <span className="text-white font-bold text-lg">2</span>
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-gray-800">Workshop 2: Sources de risque</h2>
                          <p className="text-gray-600">Identification des événements redoutés et sources de risque</p>
                        </div>
                      </div>

                      {/* Row 2.1: Events Analysis */}
                      <div className="grid grid-cols-1 ">
                        {/* Left: Events by Pillar */}
                        <motion.div variants={itemVariants}>
                          {(securityPillarEventsData.length > 0) ? (
                            <ChartContainer title={t('dashboard.charts.eventsByPillar')} icon={AlertTriangle}>
                              <div className="space-y-6">
                                {/* Header with Summary */}
                                <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-4 border border-orange-200">
                                  <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-lg font-semibold text-orange-800 flex items-center">
                                      <AlertTriangle size={20} className="mr-3 text-orange-600" />
                                      Distribution des Événements par Pilier
                                    </h4>
                                    <div className="text-right">
                                      <div className="text-2xl font-bold text-orange-700">
                                        {securityPillarEventsData.reduce((sum, item) => sum + item.value, 0)}
                                      </div>
                                      <div className="text-xs text-orange-600 font-medium">
                                        Total événements
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-sm text-orange-700">
                                    Analyse de la couverture des piliers de sécurité par les événements redoutés
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
                                  {[
                                    { name: 'Catastrophique', icon: '🔥', bg: 'bg-black', text: 'text-white' },
                                    { name: 'Critique', icon: '🚨', bg: 'bg-red-50', text: 'text-red-700' },
                                    { name: 'Majeur', icon: '⚠️', bg: 'bg-orange-50', text: 'text-orange-700' },
                                    { name: 'Modéré', icon: '🟡', bg: 'bg-amber-50', text: 'text-amber-700' },
                                    { name: 'Mineur', icon: '🟢', bg: 'bg-green-50', text: 'text-green-700' }
                                  ].map((severityType, index) => {
                                    const severityData = eventsBySeverityData.find(item => item.name === severityType.name);
                                    const count = severityData ? severityData.value : 0;

                                    if (count === 0) return null;

                                    return (
                                      <motion.div
                                        key={severityType.name}
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: index * 0.1 }}
                                        className={`${severityType.bg} rounded-lg p-2 border shadow-sm`}
                                      >
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center space-x-1">
                                            <span className="text-sm">{severityType.icon}</span>
                                            <span className={`text-xs font-medium ${severityType.text}`}>
                                              {severityType.name.substring(0, 6)}
                                            </span>
                                          </div>
                                          <div className={`text-sm font-bold ${severityType.text}`}>
                                            {count}
                                          </div>
                                        </div>
                                      </motion.div>
                                    );
                                  }).filter(Boolean)}
                                </div>
                                {/* Enhanced Donut Chart */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
                                  {/* Donut Chart */}
                                  <div className="h-[320px] relative">
                                    <ResponsiveContainer width="100%" height="100%">
                                      <PieChart>
                                        <defs>
                                          <linearGradient id="pillarGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#F59E0B" />
                                            <stop offset="100%" stopColor="#D97706" />
                                          </linearGradient>
                                          <linearGradient id="pillarGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#EF4444" />
                                            <stop offset="100%" stopColor="#DC2626" />
                                          </linearGradient>
                                          <linearGradient id="pillarGrad3" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#8B5CF6" />
                                            <stop offset="100%" stopColor="#7C3AED" />
                                          </linearGradient>
                                          <linearGradient id="pillarGrad4" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#10B981" />
                                            <stop offset="100%" stopColor="#059669" />
                                          </linearGradient>
                                          <linearGradient id="pillarGrad5" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#3B82F6" />
                                            <stop offset="100%" stopColor="#1D4ED8" />
                                          </linearGradient>
                                          <filter id="pillarShadow">
                                            <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.1)" />
                                          </filter>
                                        </defs>

                                        <Pie
                                          data={securityPillarEventsData}
                                          cx="50%"
                                          cy="50%"
                                          outerRadius={110}
                                          innerRadius={60}
                                          paddingAngle={2}
                                          dataKey="value"
                                          animationDuration={1800}
                                          animationBegin={200}
                                        >
                                          {securityPillarEventsData.map((entry, index) => {
                                            const gradients = [
                                              'url(#pillarGrad1)', 'url(#pillarGrad2)',
                                              'url(#pillarGrad3)', 'url(#pillarGrad4)', 'url(#pillarGrad5)'
                                            ];
                                            return (
                                              <Cell
                                                key={`pillar-${index}`}
                                                fill={gradients[index % gradients.length]}
                                                stroke="#ffffff"
                                                strokeWidth={2}
                                                filter="url(#pillarShadow)"
                                              />
                                            );
                                          })}
                                        </Pie>
                                        <Tooltip
                                          contentStyle={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                            border: 'none',
                                            borderRadius: '12px',
                                            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15)',
                                            padding: '16px',
                                            zIndex: 10000
                                          }}
                                          formatter={(value, name, props) => {
                                            const data = props.payload;
                                            const total = securityPillarEventsData.reduce((sum, item) => sum + item.value, 0);
                                            const percentage = ((value / total) * 100).toFixed(1);

                                            const pillarInfo = {
                                              'Gouvernance': {
                                                description: 'Événements liés à la gouvernance et au management de la sécurité',
                                                color: 'text-purple-600',
                                                bgColor: 'bg-purple-500'
                                              },
                                              'Protection': {
                                                description: 'Événements concernant les mesures de protection technique',
                                                color: 'text-blue-600',
                                                bgColor: 'bg-blue-500'
                                              },
                                              'Défense': {
                                                description: 'Événements relatifs à la détection et à la réponse aux incidents',
                                                color: 'text-red-600',
                                                bgColor: 'bg-red-500'
                                              },
                                              'Résilience': {
                                                description: 'Événements impactant la continuité et la récupération',
                                                color: 'text-green-600',
                                                bgColor: 'bg-green-500'
                                              }
                                            };

                                            const info = pillarInfo[data.subject] || {
                                              description: 'Pilier de sécurité non catégorisé',
                                              color: 'text-gray-600',
                                              bgColor: 'bg-gray-500'
                                            };

                                            return [
                                              <div key="pillar-tooltip" className="space-y-4 min-w-[300px]">
                                                <div className="flex items-center justify-between">
                                                  <div className="font-bold text-gray-800 text-lg">
                                                    {data.subject}
                                                  </div>
                                                  <div className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-medium">
                                                    Workshop 2
                                                  </div>
                                                </div>

                                                <div className="flex items-center space-x-3">
                                                  <div className={`text-3xl font-bold ${info.color}`}>
                                                    {value}
                                                  </div>
                                                  <div className="text-sm text-gray-600">
                                                    événement{value > 1 ? 's' : ''} redouté{value > 1 ? 's' : ''}
                                                  </div>
                                                </div>

                                                <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                                                  {info.description}
                                                </div>

                                                <div className="flex items-center justify-between text-sm">
                                                  <span className="text-gray-600">Impact sur le pilier:</span>
                                                  <span className="font-semibold text-gray-800">{percentage}%</span>
                                                </div>

                                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                                  <div
                                                    className={`h-full ${info.bgColor} rounded-full transition-all duration-500`}
                                                    style={{ width: `${percentage}%` }}
                                                  ></div>
                                                </div>
                                              </div>,
                                              ''
                                            ];
                                          }}
                                        />
                                      </PieChart>
                                    </ResponsiveContainer>

                                    {/* Center Label */}
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                      <div className="text-center bg-white rounded-full p-4 shadow-lg border-2 border-orange-100">
                                        <div className="text-2xl font-bold text-gray-800">
                                          {securityPillarEventsData.reduce((sum, item) => sum + item.value, 0)}
                                        </div>
                                        <div className="text-xs text-gray-500 font-medium">
                                          Événements
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Legend & Details */}
                                  <div className="space-y-3">
                                    {securityPillarEventsData.map((item, index) => {
                                      const colors = [
                                        'from-amber-500 to-amber-600',
                                        'from-red-500 to-red-600',
                                        'from-purple-500 to-purple-600',
                                        'from-green-500 to-green-600',
                                        'from-blue-500 to-blue-600'
                                      ];
                                      const percentage = ((item.value / securityPillarEventsData.reduce((sum, i) => sum + i.value, 0)) * 100).toFixed(1);
                                      return (
                                        <motion.div
                                          key={item.subject}
                                          initial={{ opacity: 0, x: 20 }}
                                          animate={{ opacity: 1, x: 0 }}
                                          transition={{ delay: index * 0.1 }}
                                          className="flex items-center justify-between p-3 rounded-xl bg-white border border-gray-100 hover:shadow-md transition-all duration-200"
                                        >
                                          <div className="flex items-center space-x-3">
                                            <div className={`w-4 h-4 rounded-full bg-gradient-to-r ${colors[index % colors.length]} shadow-sm`}></div>
                                            <div>
                                              <div className="font-medium text-gray-800 text-sm">
                                                {item.subject}
                                              </div>
                                              <div className="text-xs text-gray-500">{percentage}% du total</div>
                                            </div>
                                          </div>
                                          <div className="text-right">
                                            <div className="text-lg font-bold text-gray-800">{item.value}</div>
                                            <div className="text-xs text-gray-500">événement{item.value > 1 ? 's' : ''}</div>
                                          </div>
                                        </motion.div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            </ChartContainer>
                          ) : (
                            <EmptyChartPlaceholder title={t('dashboard.charts.eventsByPillar')} icon={BarChartLucide} />
                          )}
                        </motion.div>


                      </div>



                      {/* Row 4: Workshop 2 Charts */}
                      <motion.div variants={itemVariants}>
                        {(w2Loading) ? (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="bg-gradient-to-br from-blue-50 to-indigo-100 p-6 rounded-xl border border-blue-200 text-sm text-blue-700 flex items-center justify-center space-x-3"
                          >
                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"></div>
                            <span className="font-medium">{t('dashboard.status.loading', 'Loading Workshop 2 Data...')}</span>
                          </motion.div>
                        ) : ((w2SourcesByCategoryData.length > 0 || w2MappingsBySourceData.length > 0) ? (
                          <ChartContainer title={t('dashboard.charts.workshop2.threatCategories', 'Workshop 2: Risk Analysis')} icon={Shield}>
                            <div className="space-y-6">
                              {/* Threat Categories - Enhanced Donut Chart */}
                              <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-100">
                                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                  <div className="w-3 h-3 rounded-full bg-gradient-to-r from-red-500 to-orange-500 mr-3"></div>
                                  {t('dashboard.charts.workshop2.threatDistribution', 'Threat Category Distribution')}
                                </h3>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
                                  {/* Donut Chart */}
                                  <div className="h-[280px] relative">
                                    <ResponsiveContainer width="100%" height="100%">
                                      <PieChart>
                                        <defs>
                                          <linearGradient id="threatGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#DC2626" />
                                            <stop offset="100%" stopColor="#EF4444" />
                                          </linearGradient>
                                          <linearGradient id="threatGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#7C3AED" />
                                            <stop offset="100%" stopColor="#A855F7" />
                                          </linearGradient>
                                          <linearGradient id="threatGrad3" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#EA580C" />
                                            <stop offset="100%" stopColor="#F97316" />
                                          </linearGradient>
                                          <linearGradient id="threatGrad4" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#059669" />
                                            <stop offset="100%" stopColor="#10B981" />
                                          </linearGradient>
                                          <linearGradient id="threatGrad5" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#1D4ED8" />
                                            <stop offset="100%" stopColor="#3B82F6" />
                                          </linearGradient>
                                        </defs>

                                        <Pie
                                          data={w2SourcesByCategoryData}
                                          cx="50%"
                                          cy="50%"
                                          outerRadius={90}
                                          innerRadius={45}
                                          paddingAngle={3}
                                          dataKey="value"
                                          animationDuration={1200}
                                          animationBegin={0}
                                        >
                                          {w2SourcesByCategoryData.map((entry, index) => {
                                            const gradients = [
                                              'url(#threatGrad1)', 'url(#threatGrad2)',
                                              'url(#threatGrad3)', 'url(#threatGrad4)', 'url(#threatGrad5)'
                                            ];
                                            return (
                                              <Cell
                                                key={`w2cat-${index}`}
                                                fill={gradients[index % gradients.length]}
                                                stroke="#ffffff"
                                                strokeWidth={2}
                                              />
                                            );
                                          })}
                                        </Pie>
                                        <Tooltip
                                          contentStyle={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                            border: 'none',
                                            borderRadius: '12px',
                                            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                                            padding: '16px',
                                            maxWidth: '300px'
                                          }}
                                          formatter={(value, name, props) => {
                                            const data = props.payload;
                                            return [
                                              <div key="tooltip-content" className="space-y-2">
                                                <div className="font-semibold text-gray-800 text-sm">
                                                  {data.name}
                                                </div>
                                                <div className="text-lg font-bold text-blue-600">
                                                  {value} source{value > 1 ? 's' : ''}
                                                </div>
                                                {data.sources && data.sources.length > 0 && (
                                                  <div className="border-t pt-2 mt-2">
                                                    <div className="text-xs font-medium text-gray-600 mb-1">
                                                      Exemples de sources:
                                                    </div>
                                                    {data.sources.slice(0, 3).map((source, idx) => (
                                                      <div key={idx} className="text-xs text-gray-700 mb-1">
                                                        • <span className="font-medium">{source.name}</span>
                                                        {source.description && (
                                                          <div className="text-gray-500 ml-2 truncate">
                                                            {source.description.length > 50
                                                              ? source.description.substring(0, 50) + '...'
                                                              : source.description
                                                            }
                                                          </div>
                                                        )}
                                                      </div>
                                                    ))}
                                                    {data.sources.length > 3 && (
                                                      <div className="text-xs text-gray-500 italic">
                                                        ... et {data.sources.length - 3} autre{data.sources.length - 3 > 1 ? 's' : ''}
                                                      </div>
                                                    )}
                                                  </div>
                                                )}
                                              </div>,
                                              ''
                                            ];
                                          }}
                                        />
                                      </PieChart>
                                    </ResponsiveContainer>

                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                      <div className="text-center bg-white rounded-full p-4 shadow-lg border-2 border-gray-100">
                                        <div className="text-2xl font-bold text-gray-800">
                                          {w2SourcesByCategoryData.reduce((sum, item) => sum + item.value, 0)}
                                        </div>
                                        <div className="text-xs text-gray-500 font-medium">
                                          {t('dashboard.charts.workshop2.totalSources', 'Total Sources')}
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Legend & Stats */}
                                  <div className="space-y-3">
                                    {w2SourcesByCategoryData.map((item, index) => {
                                      const colors = [
                                        'from-red-500 to-red-600',
                                        'from-purple-500 to-purple-600',
                                        'from-orange-500 to-orange-600',
                                        'from-green-500 to-green-600',
                                        'from-blue-500 to-blue-600'
                                      ];
                                      const percentage = ((item.value / w2SourcesByCategoryData.reduce((sum, i) => sum + i.value, 0)) * 100).toFixed(1);
                                      return (
                                        <motion.div
                                          key={item.name}
                                          initial={{ opacity: 0, x: 20 }}
                                          animate={{ opacity: 1, x: 0 }}
                                          transition={{ delay: index * 0.1 }}
                                          className="flex items-center justify-between p-3 rounded-lg bg-white border border-gray-100 hover:shadow-md transition-all duration-200"
                                        >
                                          <div className="flex items-center space-x-3">
                                            <div className={`w-4 h-4 rounded-full bg-gradient-to-r ${colors[index % colors.length]} shadow-sm`}></div>
                                            <div>
                                              <div className="font-medium text-gray-800 text-sm truncate max-w-[120px]">
                                                {item.name}
                                              </div>
                                              <div className="text-xs text-gray-500">{percentage}%</div>
                                            </div>
                                          </div>
                                          <div className="text-right">
                                            <div className="text-lg font-bold text-gray-800">{item.value}</div>
                                            <div className="text-xs text-gray-500">sources</div>
                                          </div>
                                        </motion.div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>

                              {/* Risk Sources - Enhanced Horizontal Chart */}
                              <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-100">
                                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center justify-between">
                                  <div className="flex items-center">
                                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 mr-3"></div>
                                    {t('dashboard.charts.workshop2.topRiskSources', 'Top Risk Sources by Activity')}
                                  </div>
                                  <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-medium">
                                    Top {w2MappingsBySourceData.length}
                                  </span>
                                </h3>

                                {w2MappingsBySourceData.length > 0 ? (
                                  <div className="h-[400px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                      <BarChart
                                        data={w2MappingsBySourceData}
                                        layout="vertical"
                                        margin={{ top: 10, right: 30, left: 5, bottom: 10 }}
                                      >
                                        <defs>
                                          <linearGradient id="criticalGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                            <stop offset="0%" stopColor="#DC2626" />
                                            <stop offset="50%" stopColor="#EF4444" />
                                            <stop offset="100%" stopColor="#F87171" />
                                          </linearGradient>
                                          <linearGradient id="moderateGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                            <stop offset="0%" stopColor="#D97706" />
                                            <stop offset="50%" stopColor="#F59E0B" />
                                            <stop offset="100%" stopColor="#FBBF24" />
                                          </linearGradient>
                                          <linearGradient id="lowGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                            <stop offset="0%" stopColor="#059669" />
                                            <stop offset="50%" stopColor="#10B981" />
                                            <stop offset="100%" stopColor="#34D399" />
                                          </linearGradient>
                                        </defs>

                                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />

                                        <XAxis
                                          type="number"
                                          allowDecimals={false}
                                          tick={{ fontSize: 11, fill: '#4B5563' }}
                                          axisLine={false}
                                          tickLine={false}
                                        />

                                        <YAxis
                                          type="category"
                                          dataKey="name"
                                          width={140}
                                          tick={{ fontSize: 10, fill: '#4B5563' }}
                                          axisLine={false}
                                          tickLine={false}
                                          tickFormatter={(value) => {
                                            return value.length > 18 ? value.substring(0, 18) + '...' : value;
                                          }}
                                        />

                                        <Tooltip
                                          contentStyle={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                            border: '1px solid #E5E7EB',
                                            borderRadius: '12px',
                                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                                            padding: '16px',
                                            maxWidth: '300px',
                                            minWidth: '250px',
                                            zIndex: 9999,
                                            position: 'relative',
                                            backdropFilter: 'blur(8px)',
                                            fontSize: '13px',
                                            lineHeight: '1.4'
                                          }}
                                          wrapperStyle={{
                                            zIndex: 9999,
                                            position: 'relative'
                                          }}
                                          formatter={(value, name, props) => {
                                            const data = props.payload;
                                            const riskLabels = {
                                              3: { label: 'CRITIQUE', color: 'text-red-600', icon: '🔴', bg: 'bg-red-50' },
                                              2: { label: 'MODÉRÉ', color: 'text-amber-600', icon: '🟡', bg: 'bg-amber-50' },
                                              1: { label: 'FAIBLE', color: 'text-green-600', icon: '🟢', bg: 'bg-green-50' }
                                            };
                                            const riskInfo = riskLabels[data.riskLevel] || riskLabels[1];

                                            return [
                                              <div key="source-tooltip-single" className="space-y-3 text-sm">
                                                <div className="border-b border-gray-200 pb-2">
                                                  <div className="font-semibold text-gray-800 text-sm mb-1 leading-tight">
                                                    {data.name && data.name.length > 30 ? data.name.substring(0, 30) + '...' : data.name || 'Source inconnue'}
                                                  </div>
                                                  <div className="flex items-center justify-between">
                                                    <span className={`text-xs font-bold ${riskInfo.color} flex items-center px-2 py-1 ${riskInfo.bg} rounded-full`}>
                                                      {riskInfo.icon} {riskInfo.label}
                                                    </span>
                                                    <span className="text-lg font-bold text-blue-600">
                                                      {value} activité{value > 1 ? 's' : ''}
                                                    </span>
                                                  </div>
                                                </div>

                                                {data.description && data.description.trim() && (
                                                  <div className="text-xs text-gray-600 italic leading-relaxed">
                                                    {data.description.length > 80
                                                      ? data.description.substring(0, 80) + '...'
                                                      : data.description}
                                                  </div>
                                                )}

                                                <div className="grid grid-cols-1 gap-2 text-xs">
                                                  <div className="flex justify-between items-center py-1">
                                                    <span className="text-gray-500 font-medium">Catégorie:</span>
                                                    <span className="font-medium text-gray-800 capitalize text-right">
                                                      {data.category && data.category.length > 15
                                                        ? data.category.substring(0, 15) + '...'
                                                        : data.category || 'Non défini'}
                                                    </span>
                                                  </div>
                                                  {data.objectifVise && data.objectifVise.trim() && (
                                                    <div className="flex justify-between items-start py-1">
                                                      <span className="text-gray-500 font-medium">Objectif:</span>
                                                      <span className="font-medium text-gray-800 text-right leading-tight max-w-[140px]">
                                                        {data.objectifVise.length > 25
                                                          ? data.objectifVise.substring(0, 25) + '...'
                                                          : data.objectifVise}
                                                      </span>
                                                    </div>
                                                  )}
                                                </div>

                                                <div className="grid grid-cols-3 gap-2 text-xs pt-2 border-t border-gray-200">
                                                  <div className="text-center">
                                                    <div className="text-gray-500">Motivation</div>
                                                    <div className="font-medium">{data.motivation || 'N/A'}</div>
                                                  </div>
                                                  <div className="text-center">
                                                    <div className="text-gray-500">Activité</div>
                                                    <div className="font-medium">{data.activite || 'N/A'}</div>
                                                  </div>
                                                  <div className="text-center">
                                                    <div className="text-gray-500">Ressources</div>
                                                    <div className="font-medium">{data.ressources || 'N/A'}</div>
                                                  </div>
                                                </div>
                                              </div>,
                                              ''
                                            ];
                                          }}
                                          cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
                                          allowEscapeViewBox={{ x: true, y: true }}
                                          position={{ x: 'auto', y: 'auto' }}
                                        />

                                        <Bar
                                          dataKey="value"
                                          radius={[0, 4, 4, 0]}
                                          animationDuration={1200}
                                          animationBegin={300}
                                        >
                                          {w2MappingsBySourceData.map((entry, index) => {
                                            const fillColor =
                                              entry.riskLevel === 3 ? 'url(#criticalGrad)' :
                                                entry.riskLevel === 2 ? 'url(#moderateGrad)' :
                                                  'url(#lowGrad)';

                                            return (
                                              <Cell
                                                key={`source-${index}`}
                                                fill={fillColor}
                                                stroke="#ffffff"
                                                strokeWidth={1}
                                              />
                                            );
                                          })}
                                        </Bar>
                                      </BarChart>
                                    </ResponsiveContainer>

                                    <div className="flex justify-center mt-4 space-x-6 text-xs">
                                      <div className="flex items-center space-x-1">
                                        <div className="w-3 h-3 rounded bg-gradient-to-r from-red-500 to-red-600"></div>
                                        <span className="text-gray-600 font-medium">Critique</span>
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        <div className="w-3 h-3 rounded bg-gradient-to-r from-amber-500 to-amber-600"></div>
                                        <span className="text-gray-600 font-medium">Modéré</span>
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        <div className="w-3 h-3 rounded bg-gradient-to-r from-green-500 to-green-600"></div>
                                        <span className="text-gray-600 font-medium">Faible</span>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="h-[200px] flex flex-col items-center justify-center text-center bg-gray-50 rounded-lg">
                                    <AlertTriangle size={36} className="text-gray-300 mb-2" />
                                    <p className="text-gray-500 text-sm">
                                      Aucune donnée de source de risque disponible
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </ChartContainer>
                        ) : (
                          <EmptyChartPlaceholder title={t('dashboard.charts.workshop2.threatCategories', 'Workshop 2')} icon={Shield} />
                        ))}
                      </motion.div>
                      </motion.div>
                    )}

                    {/* WORKSHOP 3: Scénarios stratégiques - Tremor Version */}
                    <DashboardSection
                      title={(
                        <>
                          <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl shadow-lg">
                            <span className="text-white font-bold text-lg">3</span>
                          </div>
                          <div>
                            <h2 className="text-2xl font-bold text-gray-800">{t('dashboard.workshop3.title', 'Workshop 3: Scénarios stratégiques')}</h2>
                            <p className="text-gray-600">{t('dashboard.workshop3.subtitle', 'Analyse de l\'écosystème et cartographie des parties prenantes')}</p>
                          </div>
                        </>
                      )}
                      editMode={!!layoutEdit.w3}
                      onToggleEdit={() => toggleLayoutEdit('w3')}
                      onReset={resetW3Layout}
                      onShowAll={showAllW3}
                      hiddenCount={w3HiddenCount}
                    >
                      {/* New Tremor-based Professional Dashboard */}
                      <Workshop3Dashboard
                        stakeholdersData={stakeholdersData}
                        stakeholdersLoading={stakeholdersLoading}
                        thresholds={stakeholderThresholds}
                        measuresData={ecosystemMeasuresData}
                        measuresLoading={ecosystemMeasuresLoading}
                        attackPathsData={attackPathsData}
                        suggestedThresholds={computeSuggestedThresholds(stakeholdersData)}
                        editMode={!!layoutEdit.w3}
                        layouts={w3Layouts}
                        onLayoutChange={handleW3LayoutChange}
                        onToggleHidden={toggleW3Hidden}
                      />
                    </DashboardSection>

                    {/* WORKSHOP 3 LEGACY: Original inline charts (hidden) */}
                    {false && (
                      <motion.div variants={itemVariants} className="space-y-8 hidden">
                      <div className="flex items-center space-x-4 mb-6">
                        <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl shadow-lg">
                          <span className="text-white font-bold text-lg">3</span>
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-gray-800">Workshop 3: Scénarios stratégiques</h2>
                          <p className="text-gray-600">Analyse de l'écosystème et cartographie des parties prenantes</p>
                        </div>
                      </div>

                      {/* Row 3.1: Ecosystem Analysis */}
                      <motion.div variants={itemVariants}>
                        <ChartContainer title="Analyse de l'Écosystème (Atelier 3)" icon={Users}>
                          {stakeholdersLoading ? (
                            <div className="h-[400px] flex items-center justify-center">
                              <div className="text-center">
                                <RefreshCw size={32} className="animate-spin text-teal-600 mx-auto mb-3" />
                                <p className="text-gray-600">Chargement des données d'écosystème...</p>
                              </div>
                            </div>
                          ) : stakeholdersData.length === 0 ? (
                            <div className="h-[400px] flex flex-col items-center justify-center text-center bg-gray-50 rounded-lg">
                              <Users size={48} className="text-gray-300 mb-3" />
                              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune partie prenante</h3>
                              <p className="text-gray-500 mb-4">
                                Aucune donnée d'écosystème disponible. Complétez l'Atelier 3 pour cartographier les parties prenantes.
                              </p>
                              <button
                                onClick={() => switchToTab('ATELIER3_ACTIVITE1')}
                                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                              >
                                Aller à l'Atelier 3
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-6">
                              {/* Compact Ecosystem Overview with Embedded Info */}
                              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-5 border border-teal-200">
                                <div className="flex items-center justify-between mb-4">
                                  <h4 className="text-xl font-semibold text-teal-800 flex items-center">
                                    <Users size={24} className="mr-3 text-teal-600" />
                                    Cartographie de l'Écosystème
                                  </h4>
                                  <div className="text-right">
                                    <div className="text-3xl font-bold text-teal-700">
                                      {stakeholdersData.length || 12}
                                    </div>
                                    <div className="text-sm text-teal-600 font-medium">
                                      Parties prenantes
                                    </div>
                                  </div>
                                </div>

                                {/* Compact Info Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                  {/* Threat Zones */}
                                  <div className="bg-white rounded-lg p-3 border border-teal-100">
                                    <h6 className="text-sm font-semibold text-teal-800 mb-2 flex items-center">
                                      <div className="w-2 h-2 rounded-full bg-red-500 mr-2"></div>
                                      Zones de Menace
                                    </h6>
                                    <div className="grid grid-cols-2 gap-1 text-xs">
                                      {threatZoneDistribution.map(zone => {
                                        const zoneConfig = {
                                          danger: { label: 'Danger', color: 'bg-red-500' },
                                          control: { label: 'Contrôle', color: 'bg-amber-500' },
                                          watch: { label: 'Veille', color: 'bg-teal-500' },
                                          outside: { label: 'Hors périmètre', color: 'bg-gray-500' }
                                        };
                                        const config = zoneConfig[zone.zone] || { label: zone.zone, color: 'bg-gray-500' };

                                        return (
                                          <div key={zone.zone} className="flex items-center justify-between">
                                            <div className="flex items-center">
                                              <div className={`w-2 h-2 rounded-full ${config.color} mr-1`}></div>
                                              <span className="text-gray-600">{config.label}</span>
                                            </div>
                                            <span className="font-semibold text-teal-700">{zone.percentage}%</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>

                                  {/* Type Distribution (Interne/Externe) */}
                                  <div className="bg-white rounded-lg p-3 border border-teal-100">
                                    <h6 className="text-sm font-semibold text-teal-800 mb-2 flex items-center">
                                      <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                                      Répartition I/E
                                    </h6>
                                    <div className="space-y-1 text-xs">
                                      {[
                                        {
                                          type: 'internal',
                                          label: 'Internes',
                                          count: stakeholdersData.filter(s => s.type === 'internal').length,
                                          color: 'text-blue-700'
                                        },
                                        {
                                          type: 'external',
                                          label: 'Externes',
                                          count: stakeholdersData.filter(s => s.type === 'external').length,
                                          color: 'text-purple-700'
                                        }
                                      ].map((item) => (
                                        <div key={item.type} className="flex items-center justify-between">
                                          <span className="text-gray-600">{item.label}</span>
                                          <div className="flex items-center">
                                            <span className={`font-semibold ${item.color} mr-1`}>{item.count}</span>
                                            <span className="text-gray-500">({stakeholdersData.length > 0 ? Math.round((item.count / stakeholdersData.length) * 100) : 0}%)</span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>

                                  {/* Retention Status */}
                                  <div className="bg-white rounded-lg p-3 border border-teal-100">
                                    <h6 className="text-sm font-semibold text-teal-800 mb-2 flex items-center">
                                      <div className="w-2 h-2 rounded-full bg-emerald-500 mr-2"></div>
                                      Statut Rétention
                                    </h6>
                                    <div className="space-y-1 text-xs">
                                      {[
                                        {
                                          status: true,
                                          label: 'Retenues',
                                          count: stakeholdersData.filter(s => s.retained === true).length,
                                          color: 'text-emerald-700',
                                          icon: '✓'
                                        },
                                        {
                                          status: false,
                                          label: 'Non retenues',
                                          count: stakeholdersData.filter(s => s.retained === false).length,
                                          color: 'text-gray-700',
                                          icon: '✗'
                                        }
                                      ].map((item) => (
                                        <div key={item.label} className="flex items-center justify-between">
                                          <div className="flex items-center">
                                            <span className="mr-1">{item.icon}</span>
                                            <span className="text-gray-600">{item.label}</span>
                                          </div>
                                          <div className="flex items-center">
                                            <span className={`font-semibold ${item.color} mr-1`}>{item.count}</span>
                                            <span className="text-gray-500">({stakeholdersData.length > 0 ? Math.round((item.count / stakeholdersData.length) * 100) : 0}%)</span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>

                                {/* Compact Ecosystem Measures */}
                                <div className="bg-white rounded-lg p-3 border border-teal-100">
                                  <h6 className="text-sm font-semibold text-teal-800 mb-3 flex items-center">
                                    <Shield size={16} className="mr-2 text-teal-600" />
                                    Mesures d'Écosystème
                                    {ecosystemMeasuresLoading && (
                                      <RefreshCw size={12} className="animate-spin text-teal-600 ml-2" />
                                    )}
                                  </h6>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    {ecosystemMeasuresStats.map((measure, index) => (
                                      <div key={measure.label} className="text-center bg-gray-50 rounded-lg p-2">
                                        <div className="text-lg mb-1">{measure.icon}</div>
                                        <div className={`text-lg font-bold ${measure.color}`}>
                                          {ecosystemMeasuresLoading ? '...' : measure.count}
                                        </div>
                                        <div className="text-xs text-gray-600">{measure.label}</div>
                                      </div>
                                    ))}
                                  </div>
                                  {ecosystemMeasuresData.length === 0 && !ecosystemMeasuresLoading && (
                                    <div className="text-center text-xs text-gray-500 mt-2">
                                      Aucune mesure d'écosystème définie
                                    </div>
                                  )}
                                </div>
                              </div>
                              {/* Ecosystem Charts Grid */}
                              <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                                {/* Left: Stakeholder Categories */}
                                <div className="space-y-4">
                                  <h5 className="text-lg font-semibold text-gray-800 flex items-center">
                                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-teal-500 to-cyan-500 mr-3"></div>
                                    Répartition par Catégorie
                                  </h5>

                                  <div className="h-[350px] relative">
                                    <ResponsiveContainer width="100%" height="100%">
                                      <PieChart>
                                        <defs>
                                          <linearGradient id="stakeholderGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#3B82F6" />
                                            <stop offset="100%" stopColor="#1D4ED8" />
                                          </linearGradient>
                                          <linearGradient id="stakeholderGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#10B981" />
                                            <stop offset="100%" stopColor="#059669" />
                                          </linearGradient>
                                          <linearGradient id="stakeholderGrad3" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#F59E0B" />
                                            <stop offset="100%" stopColor="#D97706" />
                                          </linearGradient>
                                          <linearGradient id="stakeholderGrad4" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#8B5CF6" />
                                            <stop offset="100%" stopColor="#7C3AED" />
                                          </linearGradient>
                                          <linearGradient id="stakeholderGrad5" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#EC4899" />
                                            <stop offset="100%" stopColor="#DB2777" />
                                          </linearGradient>
                                          <linearGradient id="stakeholderGrad6" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#6366F1" />
                                            <stop offset="100%" stopColor="#4F46E5" />
                                          </linearGradient>
                                        </defs>

                                        <Pie
                                          data={stakeholderCategoriesData}
                                          dataKey="value"
                                          cx="50%"
                                          cy="50%"
                                          outerRadius={110}
                                          innerRadius={60}
                                          paddingAngle={2}
                                          animationDuration={1500}
                                          animationBegin={100}
                                        >
                                          {[
                                            'url(#stakeholderGrad1)', 'url(#stakeholderGrad2)',
                                            'url(#stakeholderGrad3)', 'url(#stakeholderGrad4)',
                                            'url(#stakeholderGrad5)', 'url(#stakeholderGrad6)'
                                          ].map((gradient, index) => (
                                            <Cell
                                              key={`stakeholder-${index}`}
                                              fill={gradient}
                                              stroke="#ffffff"
                                              strokeWidth={2}
                                            />
                                          ))}
                                        </Pie>
                                        <Tooltip
                                          contentStyle={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                            border: 'none',
                                            borderRadius: '12px',
                                            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15)',
                                            padding: '16px',
                                            zIndex: 10000
                                          }}
                                          formatter={(value, name) => {
                                            const total = stakeholderCategoriesData.reduce((sum, item) => sum + item.value, 0);
                                            const percentage = ((value / total) * 100).toFixed(1);

                                            const categoryInfo = {
                                              'Interne': {
                                                description: 'Parties prenantes internes à l\'organisation',
                                                color: 'text-blue-600',
                                                bgColor: 'bg-blue-500',
                                                examples: 'Employés, direction, services IT'
                                              },
                                              'Externe': {
                                                description: 'Parties prenantes externes à l\'organisation',
                                                color: 'text-green-600',
                                                bgColor: 'bg-green-500',
                                                examples: 'Clients, fournisseurs, partenaires'
                                              },
                                              'Régulateur': {
                                                description: 'Organismes de régulation et de contrôle',
                                                color: 'text-amber-600',
                                                bgColor: 'bg-amber-500',
                                                examples: 'ANSSI, CNIL, autorités sectorielles'
                                              },
                                              'Malveillant': {
                                                description: 'Acteurs malveillants potentiels',
                                                color: 'text-red-600',
                                                bgColor: 'bg-red-500',
                                                examples: 'Cybercriminels, concurrents, hacktivistes'
                                              }
                                            };

                                            const info = categoryInfo[name] || {
                                              description: 'Catégorie de partie prenante non définie',
                                              color: 'text-gray-600',
                                              bgColor: 'bg-gray-500',
                                              examples: 'À définir lors de l\'analyse'
                                            };

                                            return [
                                              <div key="stakeholder-tooltip" className="space-y-4 min-w-[320px]">
                                                <div className="flex items-center justify-between">
                                                  <div className="font-bold text-gray-800 text-lg">
                                                    {name}
                                                  </div>
                                                  <div className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-full font-medium">
                                                    Workshop 3
                                                  </div>
                                                </div>

                                                <div className="flex items-center space-x-3">
                                                  <div className={`text-3xl font-bold ${info.color}`}>
                                                    {value}
                                                  </div>
                                                  <div className="text-sm text-gray-600">
                                                    partie{value > 1 ? 's' : ''} prenante{value > 1 ? 's' : ''}
                                                  </div>
                                                </div>

                                                <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                                                  <div className="font-medium mb-1">{info.description}</div>
                                                  <div className="text-xs text-gray-600">
                                                    Exemples: {info.examples}
                                                  </div>
                                                </div>

                                                <div className="flex items-center justify-between text-sm">
                                                  <span className="text-gray-600">Répartition:</span>
                                                  <span className="font-semibold text-gray-800">{percentage}%</span>
                                                </div>

                                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                                  <div
                                                    className={`h-full ${info.bgColor} rounded-full transition-all duration-500`}
                                                    style={{ width: `${percentage}%` }}
                                                  ></div>
                                                </div>
                                              </div>,
                                              ''
                                            ];
                                          }}
                                        />
                                      </PieChart>
                                    </ResponsiveContainer>

                                    {/* Center Label */}
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                      <div className="text-center bg-white rounded-full p-4 shadow-lg border-2 border-teal-100">
                                        <div className="text-2xl font-bold text-gray-800">{stakeholdersData.length || 12}</div>
                                        <div className="text-xs text-gray-500 font-medium">Stakeholders</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Right: Threat Level Distribution */}
                                <div className="space-y-4">
                                  <h5 className="text-lg font-semibold text-gray-800 flex items-center">
                                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-red-500 to-orange-500 mr-3"></div>
                                    Niveaux de Menace
                                  </h5>

                                  {/* Threat Zone Cards */}
                                  <div className="grid grid-cols-2 gap-3 mb-4">
                                    {threatZoneDistribution.map((zone, index) => {
                                      const zoneConfig = {
                                        danger: {
                                          name: 'Zone de Danger',
                                          color: 'from-red-500 to-red-600',
                                          bg: 'bg-red-50',
                                          text: 'text-red-700',
                                          border: 'border-red-200',
                                          icon: '🚨'
                                        },
                                        control: {
                                          name: 'Zone de Contrôle',
                                          color: 'from-amber-500 to-amber-600',
                                          bg: 'bg-amber-50',
                                          text: 'text-amber-700',
                                          border: 'border-amber-200',
                                          icon: '⚠️'
                                        },
                                        watch: {
                                          name: 'Zone de Veille',
                                          color: 'from-teal-500 to-teal-600',
                                          bg: 'bg-teal-50',
                                          text: 'text-teal-700',
                                          border: 'border-teal-200',
                                          icon: '👁️'
                                        },
                                        outside: {
                                          name: 'Hors Périmètre',
                                          color: 'from-gray-500 to-gray-600',
                                          bg: 'bg-gray-50',
                                          text: 'text-gray-700',
                                          border: 'border-gray-200',
                                          icon: '📍'
                                        }
                                      };

                                      const config = zoneConfig[zone.zone] || zoneConfig.outside;

                                      return (
                                        <motion.div
                                          key={zone.zone}
                                          initial={{ opacity: 0, scale: 0.8 }}
                                          animate={{ opacity: 1, scale: 1 }}
                                          transition={{ delay: index * 0.1 }}
                                          className={`${config.bg} ${config.border} rounded-lg p-3 border-2 hover:shadow-md transition-all duration-200`}
                                        >
                                          <div className="flex items-center justify-between mb-2">
                                            <span className="text-lg">{config.icon}</span>
                                            <div className={`text-xl font-bold ${config.text}`}>
                                              {zone.count}
                                            </div>
                                          </div>
                                          <div className={`text-xs font-semibold ${config.text} text-center mb-2`}>
                                            {config.name}
                                          </div>
                                          <div className={`text-xs ${config.text} text-center`}>
                                            {zone.percentage}%
                                          </div>
                                        </motion.div>
                                      );
                                    })
                                    }
                                  </div>


                                  {/* Cyber Maturity vs Trust Scatter */}
                                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                                    <h6 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                                      <div className="w-2 h-2 rounded-full bg-teal-500 mr-2"></div>
                                      Fiabilité Cyber (Maturité x Confiance)
                                    </h6>
                                    <div className="grid grid-cols-4 gap-2 text-xs">
                                      {cyberMaturityDistribution.map((item, index) => {
                                        const colorConfig = {
                                          'Critique': 'bg-red-500',
                                          'Faible': 'bg-orange-500',
                                          'Moyen': 'bg-blue-500',
                                          'Élevé': 'bg-green-500'
                                        };

                                        return (
                                          <div key={item.level} className="text-center">
                                            <div className={`${colorConfig[item.level] || 'bg-gray-500'} rounded-full w-8 h-8 flex items-center justify-center text-white font-bold mx-auto mb-1`}>
                                              {item.count}
                                            </div>
                                            <div className="text-gray-600">{item.level}</div>
                                          </div>
                                        );
                                      })
                                      }
                                    </div>
                                  </div>
                                </div>
                              </div>


                            </div>
                          )}
                        </ChartContainer>
                      </motion.div>
                      <motion.div variants={itemVariants}>
                        {(businessValuePillarsData.length > 0) ? (
                          <ChartContainer title={t('dashboard.charts.businessValuePillars')} icon={Target}>
                            <div className="space-y-6">
                              {/* Header Summary */}
                              <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-200">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="text-lg font-semibold text-emerald-800 flex items-center">
                                    <Target size={20} className="mr-3 text-emerald-600" />
                                    Piliers de Valeur Métier
                                  </h4>
                                  <div className="text-right">
                                    <div className="text-2xl font-bold text-emerald-700">
                                      {businessValuePillarsData.reduce((sum, item) => sum + item.value, 0)}
                                    </div>
                                    <div className="text-xs text-emerald-600 font-medium">
                                      Valeurs métier
                                    </div>
                                  </div>
                                </div>
                                <div className="text-sm text-emerald-700">
                                  Répartition des valeurs métier par pilier stratégique de l'organisation
                                </div>
                              </div>
                              {/* Enhanced Donut Chart Layout */}
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
                                {/* Donut Chart */}
                                <div className="h-[350px] relative">
                                  <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                      <defs>
                                        <linearGradient id="pillarValueGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
                                          <stop offset="0%" stopColor="#10B981" />
                                          <stop offset="100%" stopColor="#059669" />
                                        </linearGradient>
                                        <linearGradient id="pillarValueGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
                                          <stop offset="0%" stopColor="#3B82F6" />
                                          <stop offset="100%" stopColor="#1D4ED8" />
                                        </linearGradient>
                                        <linearGradient id="pillarValueGrad3" x1="0%" y1="0%" x2="100%" y2="100%">
                                          <stop offset="0%" stopColor="#F59E0B" />
                                          <stop offset="100%" stopColor="#D97706" />
                                        </linearGradient>
                                        <linearGradient id="pillarValueGrad4" x1="0%" y1="0%" x2="100%" y2="100%">
                                          <stop offset="0%" stopColor="#8B5CF6" />
                                          <stop offset="100%" stopColor="#7C3AED" />
                                        </linearGradient>
                                        <linearGradient id="pillarValueGrad5" x1="0%" y1="0%" x2="100%" y2="100%">
                                          <stop offset="0%" stopColor="#EF4444" />
                                          <stop offset="100%" stopColor="#DC2626" />
                                        </linearGradient>
                                        <linearGradient id="pillarValueGrad6" x1="0%" y1="0%" x2="100%" y2="100%">
                                          <stop offset="0%" stopColor="#EC4899" />
                                          <stop offset="100%" stopColor="#DB2777" />
                                        </linearGradient>
                                        <filter id="pillarValueShadow">
                                          <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="rgba(0,0,0,0.12)" />
                                        </filter>
                                      </defs>

                                      <Pie
                                        data={businessValuePillarsData}
                                        dataKey="value"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={120}
                                        innerRadius={65}
                                        paddingAngle={3}
                                        animationDuration={1800}
                                        animationBegin={100}
                                      >
                                        {businessValuePillarsData.map((entry, index) => {
                                          const gradients = [
                                            'url(#pillarValueGrad1)', 'url(#pillarValueGrad2)',
                                            'url(#pillarValueGrad3)', 'url(#pillarValueGrad4)',
                                            'url(#pillarValueGrad5)', 'url(#pillarValueGrad6)'
                                          ];
                                          return (
                                            <Cell
                                              key={`pillar-value-${index}`}
                                              fill={gradients[index % gradients.length]}
                                              stroke="#ffffff"
                                              strokeWidth={2}
                                              filter="url(#pillarValueShadow)"
                                            />
                                          );
                                        })}
                                      </Pie>
                                      <Tooltip
                                        contentStyle={{
                                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                          border: 'none',
                                          borderRadius: '12px',
                                          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15)',
                                          padding: '16px',
                                          zIndex: 10000
                                        }}
                                        formatter={(value, name, props) => {
                                          const data = props.payload;
                                          const percentage = ((value / businessValuePillarsData.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1);
                                          return [
                                            <div key="pillar-value-tooltip" className="space-y-2">
                                              <div className="font-semibold text-gray-800 text-sm">
                                                {data.name}
                                              </div>
                                              <div className="text-xl font-bold text-emerald-600">
                                                {value} valeur{value > 1 ? 's' : ''}
                                              </div>
                                              <div className="text-xs text-gray-600">
                                                {percentage}% du total
                                              </div>
                                            </div>,
                                            ''
                                          ];
                                        }}
                                      />
                                    </PieChart>
                                  </ResponsiveContainer>

                                  {/* Center Label */}
                                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <div className="text-center bg-white rounded-full p-4 shadow-lg border-2 border-emerald-100">
                                      <div className="text-2xl font-bold text-gray-800">
                                        {businessValuePillarsData.reduce((sum, item) => sum + item.value, 0)}
                                      </div>
                                      <div className="text-xs text-gray-500 font-medium">
                                        Piliers
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Enhanced Legend & Stats */}
                                <div className="space-y-3">
                                  <h5 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 mr-3"></div>
                                    Répartition par Pilier
                                  </h5>
                                  {businessValuePillarsData.map((item, index) => {
                                    const colors = [
                                      'from-green-500 to-green-600',
                                      'from-blue-500 to-blue-600',
                                      'from-amber-500 to-amber-600',
                                      'from-purple-500 to-purple-600',
                                      'from-red-500 to-red-600',
                                      'from-pink-500 to-pink-600'
                                    ];
                                    const percentage = ((item.value / businessValuePillarsData.reduce((sum, i) => sum + i.value, 0)) * 100).toFixed(1);

                                    return (
                                      <motion.div
                                        key={item.name}
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                        className="flex items-center justify-between p-4 rounded-xl bg-white border border-gray-100 hover:shadow-md transition-all duration-200"
                                      >
                                        <div className="flex items-center space-x-3">
                                          <div className={`w-4 h-4 rounded-full bg-gradient-to-r ${colors[index % colors.length]} shadow-sm`}></div>
                                          <div>
                                            <div className="font-medium text-gray-800 text-sm">
                                              {item.name}
                                            </div>
                                            <div className="text-xs text-gray-500">{percentage}% du total</div>
                                          </div>
                                        </div>
                                        <div className="text-right">
                                          <div className="text-lg font-bold text-gray-800">{item.value}</div>
                                          <div className="text-xs text-gray-500">valeur{item.value > 1 ? 's' : ''}</div>
                                        </div>
                                      </motion.div>
                                    );
                                  })}

                                  {/* Key Insights */}
                                  <div className="mt-6 p-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg border border-emerald-200">
                                    <h6 className="text-sm font-semibold text-emerald-800 mb-2 flex items-center">
                                      <div className="w-2 h-2 rounded-full bg-emerald-500 mr-2"></div>
                                      Insights Clés
                                    </h6>
                                    <div className="space-y-2 text-xs text-emerald-700">
                                      <div className="flex items-center justify-between">
                                        <span>Pilier dominant:</span>
                                        <span className="font-medium">
                                          {businessValuePillarsData.reduce((max, item) =>
                                            item.value > max.value ? item : max, businessValuePillarsData[0]
                                          )?.name || 'N/A'}
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <span>Couverture:</span>
                                        <span className="font-medium">
                                          {businessValuePillarsData.length} pilier{businessValuePillarsData.length > 1 ? 's' : ''} actif{businessValuePillarsData.length > 1 ? 's' : ''}
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <span>Distribution:</span>
                                        <span className="font-medium">
                                          {businessValuePillarsData.length > 0 &&
                                            (Math.max(...businessValuePillarsData.map(p => p.value)) /
                                              Math.min(...businessValuePillarsData.map(p => p.value))).toFixed(1)
                                          }:1 ratio
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </ChartContainer>
                        ) : (
                          <EmptyChartPlaceholder title={t('dashboard.charts.businessValuePillars')} icon={Target} />
                        )}
                      </motion.div>
                    </motion.div>
                    )}

                    {/* WORKSHOP 4: Scénarios opérationnels - Tremor Version */}
                    <DashboardSection
                      title={(
                        <>
                          <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-lg">
                            <span className="text-white font-bold text-lg">4</span>
                          </div>
                          <div>
                            <h2 className="text-2xl font-bold text-gray-800">{t('dashboard.workshop4.title', 'Workshop 4: Scénarios opérationnels')}</h2>
                            <p className="text-gray-600">{t('dashboard.workshop4.subtitle', 'Analyse des risques opérationnels et scénarios d\'attaque')}</p>
                          </div>
                        </>
                      )}
                      editMode={!!layoutEdit.w4}
                      onToggleEdit={() => toggleLayoutEdit('w4')}
                      onReset={resetW4Layout}
                      onShowAll={showAllW4}
                      hiddenCount={w4HiddenCount}
                    >
                      {/* New Tremor-based Professional Dashboard */}
                      <Workshop4Dashboard
                        attackPaths={currentAttackPaths || []}
                        operationalScenarios={currentOperationalScenarios || []}
                        workshop4DashboardData={workshop4DashboardData}
                        isLoading={false}
                        editMode={!!layoutEdit.w4}
                        layouts={w4Layouts}
                        onLayoutChange={handleW4LayoutChange}
                        onToggleHidden={toggleW4Hidden}
                      />
                    </DashboardSection>

                    {/* WORKSHOP 4 LEGACY: Original inline charts (hidden) */}
                    {false && (
                      <motion.div variants={itemVariants} className="space-y-8 hidden">
                      <div className="flex items-center space-x-4 mb-6">
                        <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-lg">
                          <span className="text-white font-bold text-lg">4</span>
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-gray-800">Workshop 4: Scénarios opérationnels</h2>
                          <p className="text-gray-600">Analyse des risques opérationnels et scénarios d'attaque</p>
                        </div>
                      </div>

                      {/* Row 4.1: Risk Analysis Charts */}
                      {((!currentAttackPaths || currentAttackPaths.length === 0) && (!currentOperationalScenarios || currentOperationalScenarios.length === 0)) ? (
                        <div className="col-span-1 lg:col-span-2">
                          <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-lg overflow-hidden relative">
                            <div className="flex items-center mb-4">
                              <div className="p-2 rounded-lg bg-gradient-to-br from-purple-50 to-indigo-50 mr-3">
                                <Zap size={20} className="text-indigo-600" />
                              </div>
                              <h3 className="font-semibold text-gray-800">Workshop 4: Scénarios opérationnels</h3>
                            </div>
                            <div className="h-[300px] flex flex-col items-center justify-center text-center bg-gradient-to-b from-gray-50 to-white rounded-lg border border-gray-100">
                              <Zap size={48} className="text-gray-300 mb-3 opacity-50" />
                              <p className="text-sm text-gray-500 mb-4">Aucune donnée disponible pour le moment.</p>
                              <p className="text-xs text-gray-400 max-w-md">Les chemins d'attaque et les scénarios opérationnels doivent être générés dans l'atelier 4 pour afficher les graphiques.</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                          {(() => {
                            // Use dashboard data if available, otherwise fallback to direct data
                            const overallRiskScore = workshop4DashboardData?.overallRiskScore || 0;

                            const riskLevel = (score) => {
                              if (score >= 9) return { label: 'Critique', color: 'text-red-500' };
                              if (score >= 7) return { label: 'Élevé', color: 'text-orange-500' };
                              if (score >= 4) return { label: 'Moyen', color: 'text-yellow-500' };
                              return { label: 'Faible', color: 'text-green-500' };
                            };
                            const scoreInfo = riskLevel(overallRiskScore);

                            return (
                              <ChartContainer title="Score de Risque Opérationnel Global" icon={Zap}>
                                <div className="h-[350px] flex flex-col items-center justify-center text-center">
                                  <div className="relative w-48 h-48">
                                    <svg className="w-full h-full" viewBox="0 0 36 36">
                                      <path
                                        className="text-gray-200"
                                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                        fill="none" stroke="currentColor" strokeWidth="3" />
                                      <path
                                        className={scoreInfo.color}
                                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                        fill="none" stroke="currentColor" strokeWidth="3" strokeDasharray={`${overallRiskScore * 10}, 100`} />
                                    </svg>
                                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                                      <span className="text-5xl font-bold text-gray-800">{overallRiskScore}</span>
                                      <span className={`text-lg font-semibold ${scoreInfo.color}`}>{scoreInfo.label}</span>
                                    </div>
                                  </div>
                                  <p className="text-sm text-gray-500 mt-4 max-w-xs">
                                    Ce score représente le niveau de risque le plus élevé identifié.
                                  </p>
                                </div>
                              </ChartContainer>
                            );
                          })()}

                          {(() => {
                            // Use operational scenarios directly
                            const scenarios = Array.isArray(currentOperationalScenarios) ? currentOperationalScenarios : [];
                            const severityMap = { 'Faible': 1, 'Moyenne': 2, 'Élevée': 3, 'Critique': 4 };
                            const likelihoodMap = { 'Faible': 1, 'Moyenne': 2, 'Élevée': 3, 'Critique': 4 };
                            const chartData = scenarios.map(s => ({
                              name: s.name || 'Scénario sans nom',
                              x: likelihoodMap[s.likelihood] || 2,
                              y: severityMap[s.severity] || 2,
                              z: 100,
                              severity: s.severity || 'Moyenne',
                              likelihood: s.likelihood || 'Moyenne',
                              riskScore: (severityMap[s.severity] || 2) * (likelihoodMap[s.likelihood] || 2)
                            }));

                            const CustomTooltip = ({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
                                    <p className="font-semibold text-gray-800">{data.name}</p>
                                    <p className="text-sm text-gray-600">Sévérité: {data.severity}</p>
                                    <p className="text-sm text-gray-600">Vraisemblance: {data.likelihood}</p>
                                    <p className="text-sm text-gray-600">Score de risque: {data.riskScore}</p>
                                  </div>
                                );
                              }
                              return null;
                            };

                            return (
                              <ChartContainer title="Matrice des Scénarios d'Attaque" icon={Target}>
                                <div className="h-[350px]">
                                  <ResponsiveContainer width="100%" height="100%">
                                    <ScatterChart margin={{ top: 20, right: 30, bottom: 40, left: 40 }}>
                                      <CartesianGrid strokeDasharray="3 3" />
                                      <XAxis type="number" dataKey="x" name="Vraisemblance" domain={[0.5, 4.5]} ticks={[1, 2, 3, 4]} tickFormatter={(tick) => ['Faible', 'Moyenne', 'Élevée', 'Critique'][tick - 1]} label={{ value: 'Vraisemblance', position: 'insideBottom', offset: -15 }} />
                                      <YAxis type="number" dataKey="y" name="Sévérité" domain={[0.5, 4.5]} ticks={[1, 2, 3, 4]} tickFormatter={(tick) => ['Faible', 'Moyenne', 'Élevée', 'Critique'][tick - 1]} label={{ value: 'Sévérité', angle: -90, position: 'insideLeft', offset: -25 }} />
                                      <ZAxis type="number" dataKey="z" range={[200, 500]} name="size" />
                                      <Tooltip cursor={{ strokeDasharray: '3 3' }} content={<CustomTooltip />} />
                                      <Scatter name="Scénarios" data={chartData} fill="#8884d8">
                                        {chartData.map((entry, index) => (
                                          <Cell key={`cell-${index}`} fill={entry.riskScore >= 12 ? '#DC2626' : entry.riskScore >= 8 ? '#F59E0B' : entry.riskScore >= 4 ? '#FBBF24' : '#10B981'} />
                                        ))}
                                      </Scatter>
                                    </ScatterChart>
                                  </ResponsiveContainer>
                                </div>
                              </ChartContainer>
                            );
                          })()}

                          {(() => {
                            // Use dashboard data if available
                            const assetsData = workshop4DashboardData?.topAtRiskAssets || [];

                            if (assetsData.length === 0) return <EmptyChartPlaceholder title="Top 5 des Actifs à Risque" icon={Shield} />;

                            return (
                              <ChartContainer title="Top 5 des Actifs à Risque" icon={Shield}>
                                <div className="h-[350px]">
                                  <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={assetsData} layout="vertical" margin={{ top: 5, right: 25, left: 50, bottom: 5 }}>
                                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                      <XAxis type="number" domain={[0, 10]} tick={{ fontSize: 12 }} />
                                      <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={110} />
                                      <Tooltip
                                        cursor={{ fill: 'rgba(240, 240, 240, 0.5)' }}
                                        contentStyle={{ borderRadius: '10px', border: '1px solid #ccc' }}
                                        formatter={(value, name, props) => [
                                          `Score: ${value}`,
                                          `Type: ${props.payload.type || 'Non spécifié'}`,
                                          `Vulnérabilités: ${props.payload.vulnerabilityCount || 0}`
                                        ]}
                                      />
                                      <Bar dataKey="riskScore" barSize={20} radius={[0, 10, 10, 0]}>
                                        {assetsData.map((entry, index) => (
                                          <Cell key={`cell-${index}`} fill={entry.riskScore >= 7 ? '#DC2626' : entry.riskScore >= 5 ? '#F59E0B' : entry.riskScore >= 3 ? '#FBBF24' : '#10B981'} />
                                        ))}
                                      </Bar>
                                    </BarChart>
                                  </ResponsiveContainer>
                                </div>
                              </ChartContainer>
                            );
                          })()}

                          {(() => {
                            // Use dashboard data if available
                            const techniquesData = workshop4DashboardData?.topAttackTechniques || [];

                            if (techniquesData.length === 0) return <EmptyChartPlaceholder title="Top 5 des Techniques d'Attaque" icon={BarChartLucide} />;

                            return (
                              <ChartContainer title="Top 5 des Techniques d'Attaque" icon={BarChartLucide}>
                                <div className="h-[350px]">
                                  <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={techniquesData} layout="vertical" margin={{ top: 5, right: 25, left: 80, bottom: 5 }}>
                                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                                      <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={140} />
                                      <Tooltip
                                        cursor={{ fill: 'rgba(240, 240, 240, 0.5)' }}
                                        contentStyle={{ borderRadius: '10px', border: '1px solid #ccc' }}
                                        formatter={(value) => [`${value} occurrence${value > 1 ? 's' : ''}`, 'Fréquence']}
                                      />
                                      <Bar dataKey="count" barSize={20} fill="#8B5CF6" radius={[0, 10, 10, 0]} />
                                    </BarChart>
                                  </ResponsiveContainer>
                                </div>
                              </ChartContainer>
                            );
                          })()}
                        </div>
                      )}
                    </motion.div>
                    )}

                    {/* WORKSHOP 5: Traitement du risque */}
                    <DashboardSection
                      title={(
                        <>
                          <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg">
                            <span className="text-white font-bold text-lg">5</span>
                          </div>
                          <div>
                            <h2 className="text-2xl font-bold text-gray-800">{t('dashboard.workshop5.title', 'Atelier 5: Traitement du risque')}</h2>
                            <p className="text-gray-600">{t('dashboard.workshop5.subtitle', 'Stratégies de traitement et plan d\'action sécurité')}</p>
                          </div>
                        </>
                      )}
                      editMode={!!layoutEdit.w5}
                      onToggleEdit={() => toggleLayoutEdit('w5')}
                      onReset={resetW5Layout}
                      onShowAll={showAllW5}
                      hiddenCount={w5HiddenCount}
                    >
                      {/* Workshop 5 Charts — same rows as Récapitulatif des scénarios de risque */}
                      {workshop5Synthesis ? (
                        <Workshop5Charts editMode={!!layoutEdit.w5} layouts={w5Layouts} onLayoutChange={handleW5LayoutChange} onToggleHidden={toggleW5Hidden} />
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200 shadow-lg hover:shadow-xl transition-shadow duration-300">
                            <div className="flex items-center mb-4">
                              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <h3 className="text-lg font-semibold text-purple-800">Plan d'Action Sécurité</h3>
                            </div>
                            <p className="text-purple-600 text-sm leading-relaxed">
                              Suivi des mesures de sécurité et de leur mise en œuvre selon les priorités définies.
                            </p>
                            <div className="mt-4 flex items-center text-xs text-purple-500">
                              <div className="w-2 h-2 bg-purple-400 rounded-full mr-2"></div>
                              Implémentation et suivi des contrôles
                            </div>
                          </div>
                          <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border border-orange-200 shadow-lg hover:shadow-xl transition-shadow duration-300">
                            <div className="flex items-center mb-4">
                              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center mr-3">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                              </div>
                              <h3 className="text-lg font-semibold text-orange-800">Stratégies de Traitement</h3>
                            </div>
                            <p className="text-orange-600 text-sm leading-relaxed">
                              Répartition des stratégies : accepter, éviter, transférer, réduire les risques.
                            </p>
                            <div className="mt-4 flex items-center text-xs text-orange-500">
                              <div className="w-2 h-2 bg-orange-400 rounded-full mr-2"></div>
                              Gestion et mitigation des risques
                            </div>
                          </div>
                        </div>
                      )}
                    </DashboardSection>
                  </div>
                </motion.div>
              )}



            </div>

            {/* Footer Information */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-8 text-xs text-gray-500 flex flex-col sm:flex-row items-center gap-4 border-t border-gray-200 pt-4"
            >
              <div className="flex items-center gap-x-4 gap-y-1 flex-wrap">
                <span className="flex items-center bg-gray-100 px-3 py-1.5 rounded-full">
                  <Calendar size={14} className="mr-1.5 text-blue-500" />
                  <span className="font-medium text-gray-600">{t('dashboard.footer.organization')} :</span>
                  <span className="ml-1.5">{context?.organizationName || currentAnalysis?.companyName || 'N/A'}</span>
                </span>
                <span className="flex items-center bg-gray-100 px-3 py-1.5 rounded-full">
                  <Calendar size={14} className="mr-1.5 text-green-500" />
                  <span className="font-medium text-gray-600">{t('dashboard.footer.analysisDate')} :</span>
                  <span className="ml-1.5">{context?.analysisDate || 'N/A'}</span>
                </span>
                <span className="flex items-center bg-gray-100 px-3 py-1.5 rounded-full">
                  <RefreshCw size={14} className="mr-1.5 text-purple-500" />
                  <span className="font-medium text-gray-600">{t('dashboard.footer.lastModified')}:</span>
                  <span className="ml-1.5">{currentAnalysis?.updatedAt ? new Date(currentAnalysis.updatedAt).toLocaleString(i18n.language === 'en' ? 'en-US' : 'fr-FR') : 'N/A'}</span>
                </span>
              </div>
              <div className="flex-grow"></div>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      )}

      {/* Message when no analysis is selected */}
      {!isWorkshopDataLoading && !currentAnalysis && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-16 bg-gradient-to-b from-blue-50 to-gray-50 rounded-xl border border-blue-100 mt-6 px-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 100 }}
            className="bg-white p-6 rounded-xl shadow-lg inline-block mb-6"
          >
            <Info size={64} className="mx-auto text-blue-400" />
          </motion.div>
          <h3 className="text-xl text-gray-700 font-medium mb-2">Aucune analyse sélectionnée</h3>
          <p className="text-gray-500 max-w-md mx-auto mb-6">
            Veuillez choisir une analyse existante dans le sélecteur ci-dessus ou créez-en une nouvelle pour commencer.
          </p>

        </motion.div>
      )}
      <StrategicSaveModal
        isOpen={showStrategicSave}
        onClose={() => setShowStrategicSave(false)}
        onConfirm={handleStrategicSave}
        currentVersion={currentAnalysis?.version}
      />
    </motion.div>

  );
};
export default Dashboard;
